import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { setAdminViewMode } from "../lib/api";
import { toast } from "sonner";
import { format, subDays, startOfYear, isAfter } from "date-fns";
import { 
  Users, 
  CreditCard, 
  Crown, 
  CircleNotch, 
  Check, 
  X, 
  ArrowClockwise,
  MagnifyingGlass,
  CalendarPlus,
  Prohibit,
  Hourglass,
  SortAscending,
  SortDescending,
  Funnel,
  ShareNetwork,
  CurrencyDollar,
  Wallet,
  Copy,
  CheckCircle,
  ChartBar,
  Warning,
  Database,
  Image,
  Eye,
  CaretRight,
  CalendarBlank,
  Trash,
  EnvelopeSimple,
  Play,
  Clock,
  Compass
} from "phosphor-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SyncedScrollTable } from "@/components/ui/synced-scroll-table";
import GeniLoader from "../components/GeniLoader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const LIFECYCLE_TEST_RECIPIENT_EMAIL = "vinilang13@gmail.com";

const OPTIONS_USAGE_ITEMS = [
  ['OPTIONS_DASHBOARD_VIEWED', 'Dashboard'],
  ['OPTIONS_OPEN_POSITIONS_VIEWED', 'Open Positions'],
  ['OPTIONS_HISTORY_VIEWED', 'Histórico'],
  ['OPTIONS_PERFORMANCE_VIEWED', 'Performance'],
  ['OPTIONS_ANALYTICS_VIEWED', 'Análises'],
  ['OPTIONS_ALERTS_VIEWED', 'Alertas'],
  ['OPTIONS_SYNC_COMPLETED', 'Syncs'],
];

// Resolver event_key -> Email Templates (Phase 2E) template_id, mirrors
// lifecycle_email_service.EVENT_KEY_TO_TEMPLATE_ID on the backend. Used only to compute the
// "eligible users from the last dry-run" count shown before enabling an event's automation.
const TEMPLATE_ID_TO_EVENT_KEY = {
  activation_no_trade_7d: 'activation',
  milestone_1: 'milestone_1',
  milestone_10: 'milestone_10',
  milestone_50: 'milestone_50',
  milestone_100: 'milestone_100',
  inactive_7d: 'inactivity_7',
  inactive_14d: 'inactivity_14',
  inactive_30d: 'inactivity_30',
};

export default function Admin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [optionsUsageSummary, setOptionsUsageSummary] = useState(null);
  const [selectedOptionsActivity, setSelectedOptionsActivity] = useState(null);
  const [loadingOptionsActivity, setLoadingOptionsActivity] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [sendingSelectedReengagement, setSendingSelectedReengagement] = useState(false);
  
  // Payment time period filter
  const [paymentPeriod, setPaymentPeriod] = useState('all');
  
  // Referral state
  const [referralSummary, setReferralSummary] = useState([]);
  const [referralConversions, setReferralConversions] = useState([]);
  const [referralTotals, setReferralTotals] = useState(null);
  const [loadingReferrals, setLoadingReferrals] = useState(true);
  const [referralView, setReferralView] = useState('summary'); // 'summary' or 'conversions'
  
  // Referrer details modal state
  const [referrerDetailsModal, setReferrerDetailsModal] = useState({
    open: false,
    referrer: null,
    owedConversions: [],
    loading: false
  });
  
  // New filter and sort states for Users
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'free', 'pro_monthly', 'pro_yearly'
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'options', 'other'
  const [usageFilter, setUsageFilter] = useState('all'); // 'all', 'options', 'trades', 'both', 'none'
  const [expirationSort, setExpirationSort] = useState('none'); // 'none', 'nearest', 'latest'
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    action: null,
    variant: 'default'
  });

  // User Usage state
  const [userUsage, setUserUsage] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [projectSummary, setProjectSummary] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [selectedUserUsage, setSelectedUserUsage] = useState(null);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);
  const [usageSearchTerm, setUsageSearchTerm] = useState('');
  const [usageActivityFilter, setUsageActivityFilter] = useState('all'); // 'all', 'light', 'medium', 'heavy'

  // Product Updates Subscribers state
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberStats, setSubscriberStats] = useState({ total: 0, by_plan: { free: 0, pro: 0 } });
  const [loadingSubscribers, setLoadingSubscribers] = useState(true);
  const [subscriberPlanFilter, setSubscriberPlanFilter] = useState('all'); // 'all', 'free', 'pro'

  // Re-engagement email test state
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [sendingBulkReengagement, setSendingBulkReengagement] = useState(false);

  // Lifecycle Email Engine (Phase 2D - dry-run) state
  const [lifecycleResults, setLifecycleResults] = useState([]);
  const [lifecycleRuns, setLifecycleRuns] = useState([]);
  const [lifecycleDryRun, setLifecycleDryRun] = useState(true);
  const [loadingLifecycle, setLoadingLifecycle] = useState(true);
  const [runningLifecycleNow, setRunningLifecycleNow] = useState(false);
  const [lifecycleEventFilter, setLifecycleEventFilter] = useState('all');

  // Lifecycle Email Templates (Phase 2E - real template previews/tests) state
  const [lifecycleTemplates, setLifecycleTemplates] = useState([]);
  const [loadingLifecycleTemplates, setLoadingLifecycleTemplates] = useState(true);
  const [testingTemplateId, setTestingTemplateId] = useState(null); // template_id or 'all'
  const [templateTestLang, setTemplateTestLang] = useState('pt');

  // Lifecycle real-send automation (master switch + per-event toggles) state - both default OFF
  const [lifecycleAutomation, setLifecycleAutomation] = useState({ master_enabled: false, event_enabled: {} });
  const [loadingAutomation, setLoadingAutomation] = useState(true);
  const [togglingAutomation, setTogglingAutomation] = useState(null); // 'master' or a template_id

  // BTC Outlook State (100% manual Admin)
  const [outlookData, setOutlookData] = useState(null);
  const [loadingOutlook, setLoadingOutlook] = useState(true);
  const [selectedBias, setSelectedBias] = useState('NEUTRAL');
  const [isPublishingOutlook, setIsPublishingOutlook] = useState(false);
  
  // Futures Hedge levels state
  const [putHedgeAggressive, setPutHedgeAggressive] = useState("");
  const [putHedgeConservative, setPutHedgeConservative] = useState("");
  const [callHedgeAggressive, setCallHedgeAggressive] = useState("");
  const [callHedgeConservative, setCallHedgeConservative] = useState("");

  const fetchAdminOutlook = async () => {
    try {
      setLoadingOutlook(true);
      const res = await api.get('/options/btc-outlook');
      setOutlookData(res.data);
      if (res.data && res.data.status === 'published') {
        if (res.data.current_bias) {
          setSelectedBias(res.data.current_bias);
        }
        setPutHedgeAggressive(res.data.put_hedge_aggressive !== null && res.data.put_hedge_aggressive !== undefined ? String(res.data.put_hedge_aggressive) : "");
        setPutHedgeConservative(res.data.put_hedge_conservative !== null && res.data.put_hedge_conservative !== undefined ? String(res.data.put_hedge_conservative) : "");
        setCallHedgeAggressive(res.data.call_hedge_aggressive !== null && res.data.call_hedge_aggressive !== undefined ? String(res.data.call_hedge_aggressive) : "");
        setCallHedgeConservative(res.data.call_hedge_conservative !== null && res.data.call_hedge_conservative !== undefined ? String(res.data.call_hedge_conservative) : "");
      } else {
        setPutHedgeAggressive("");
        setPutHedgeConservative("");
        setCallHedgeAggressive("");
        setCallHedgeConservative("");
      }
    } catch (e) {
      console.error('Error fetching admin outlook:', e);
    } finally {
      setLoadingOutlook(false);
    }
  };

  const handlePublishOutlook = async () => {
    // Validações no frontend
    const parseVal = (val) => {
      if (val === null || val === undefined || String(val).trim() === "") return null;
      const num = Number(val);
      if (isNaN(num)) return "invalid";
      return num;
    };

    const putAgg = parseVal(putHedgeAggressive);
    const putCons = parseVal(putHedgeConservative);
    const callAgg = parseVal(callHedgeAggressive);
    const callCons = parseVal(callHedgeConservative);

    if (putAgg === "invalid" || putCons === "invalid" || callAgg === "invalid" || callCons === "invalid") {
      toast.error("Os níveis de hedge devem ser números válidos.");
      return;
    }

    if ((putAgg !== null && putAgg <= 0) || (putCons !== null && putCons <= 0) || (callAgg !== null && callAgg <= 0) || (callCons !== null && callCons <= 0)) {
      toast.error("Os valores de hedge devem ser maiores que zero.");
      return;
    }

    if (putAgg !== null && putCons !== null && putCons >= putAgg) {
      toast.error("Put Side: O nível de hedge Conservative deve ser menor que o Aggressive.");
      return;
    }

    if (callAgg !== null && callCons !== null && callCons <= callAgg) {
      toast.error("Call Side: O nível de hedge Conservative deve ser maior que o Aggressive.");
      return;
    }

    try {
      setIsPublishingOutlook(true);
      const res = await api.post('/options/btc-outlook', {
        current_bias: selectedBias,
        put_hedge_aggressive: putHedgeAggressive,
        put_hedge_conservative: putHedgeConservative,
        call_hedge_aggressive: callHedgeAggressive,
        call_hedge_conservative: callHedgeConservative
      });
      if (res.status === 200) {
        toast.success('BTC Outlook e Futures Hedge salvos com sucesso!');
        await fetchAdminOutlook();
      }
    } catch (e) {
      console.error('Error publishing outlook:', e);
      toast.error(e.response?.data?.detail || 'Erro ao publicar o BTC Outlook');
    } finally {
      setIsPublishingOutlook(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'outlook') {
      fetchAdminOutlook();
    }
  }, [activeTab]);

  useEffect(() => {
    fetchUsers();
    fetchPayments();
    fetchReferrals();
  }, []);

  // Fetch user usage when switching to usage tab
  useEffect(() => {
    if (activeTab === 'usage' && userUsage.length === 0) {
      fetchUserUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch subscribers when switching to subscribers tab
  useEffect(() => {
    if (activeTab === 'subscribers' && subscribers.length === 0) {
      fetchSubscribers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch lifecycle email engine dry-run data when switching to lifecycle tab
  useEffect(() => {
    if (activeTab === 'lifecycle') {
      fetchLifecycleData();
      fetchLifecycleTemplates();
      fetchLifecycleAutomation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch product update subscribers
  const fetchSubscribers = async () => {
    setLoadingSubscribers(true);
    try {
      const response = await api.get('/admin/product-updates-subscribers');
      setSubscribers(response.data.subscribers || []);
      setSubscriberStats({
        total: response.data.total || 0,
        by_plan: response.data.by_plan || { free: 0, pro: 0 }
      });
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error('Failed to load product update subscribers');
    } finally {
      setLoadingSubscribers(false);
    }
  };

  // CSV Export for product update subscribers
  const exportSubscribersCSV = () => {
    // Filter by current plan filter
    let dataToExport = subscribers;
    if (subscriberPlanFilter !== 'all') {
      dataToExport = subscribers.filter(sub => {
        if (subscriberPlanFilter === 'free') return sub.subscription_status !== 'pro';
        if (subscriberPlanFilter === 'pro') return sub.subscription_status === 'pro';
        return true;
      });
    }

    // Build CSV content
    const headers = ['email', 'created_at', 'plan'];
    const rows = dataToExport.map(sub => {
      const plan = sub.subscription_status === 'pro' 
        ? `pro_${sub.subscription_plan || 'monthly'}` 
        : 'free';
      const createdAt = sub.created_at 
        ? format(new Date(sub.created_at), 'yyyy-MM-dd HH:mm:ss')
        : '';
      return [sub.email || '', createdAt, plan];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'geni-product-updates.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${dataToExport.length} subscribers to CSV`);
  };

  // Fetch lifecycle email engine dry-run results + run history (Phase 2D)
  const fetchLifecycleData = async () => {
    setLoadingLifecycle(true);
    try {
      const [resultsRes, runsRes] = await Promise.all([
        api.get('/admin/lifecycle/dry-run-results', { params: { limit: 200 } }),
        api.get('/admin/lifecycle/runs', { params: { limit: 10 } })
      ]);
      setLifecycleResults(resultsRes.data.results || []);
      setLifecycleDryRun(resultsRes.data.dry_run !== false);
      setLifecycleRuns(runsRes.data.runs || []);
    } catch (error) {
      console.error('Error fetching lifecycle data:', error);
      toast.error('Failed to load lifecycle email dry-run data');
    } finally {
      setLoadingLifecycle(false);
    }
  };

  // Manually trigger one lifecycle dry-run evaluation cycle (never sends real emails)
  const runLifecycleNow = async () => {
    setRunningLifecycleNow(true);
    try {
      const response = await api.post('/admin/lifecycle/run-now');
      toast.success(
        `Dry-run concluído: ${response.data.users_evaluated} usuários avaliados · ` +
        `${response.data.activation_count} ativação · ${response.data.milestone_count} marcos · ${response.data.inactivity_count} inatividade · ${response.data.monthly_summary_count ?? 0} resumo mensal`
      );
      await fetchLifecycleData();
    } catch (error) {
      console.error('Error running lifecycle cycle:', error);
      toast.error('Failed to run lifecycle dry-run cycle');
    } finally {
      setRunningLifecycleNow(false);
    }
  };

  // Fetch the 8 real Lifecycle email templates (Phase 2E) for the admin table
  const fetchLifecycleTemplates = async () => {
    setLoadingLifecycleTemplates(true);
    try {
      const response = await api.get('/admin/lifecycle/templates');
      setLifecycleTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching lifecycle templates:', error);
      toast.error('Failed to load lifecycle email templates');
    } finally {
      setLoadingLifecycleTemplates(false);
    }
  };

  // Send ONE template preview to the fixed admin/test address only
  const testLifecycleTemplate = async (templateId) => {
    setTestingTemplateId(templateId);
    try {
      const response = await api.post(`/admin/lifecycle/templates/test/${templateId}`, null, { params: { lang: templateTestLang } });
      if (response.data.success) {
        toast.success(`Preview enviado para ${response.data.sent_to}`);
      } else {
        toast.error(`Falha ao enviar preview: ${response.data.error || 'erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Error testing lifecycle template:', error);
      toast.error('Failed to send template preview');
    } finally {
      setTestingTemplateId(null);
    }
  };

  // Send all 8 template previews to the fixed admin/test address only
  const testAllLifecycleTemplates = async () => {
    setTestingTemplateId('all');
    try {
      const response = await api.post('/admin/lifecycle/templates/test-all', null, { params: { lang: templateTestLang } });
      if (response.data.failed > 0) {
        const labelById = Object.fromEntries(lifecycleTemplates.map(t => [t.id, t.label]));
        const failedLabels = (response.data.results || [])
          .filter(r => !r.success)
          .map(r => labelById[r.template_id] || r.template_id)
          .join(', ');
        toast.error(`${response.data.sent} enviados, ${response.data.failed} falharam (${failedLabels})`);
      } else {
        toast.success(`${response.data.sent} lifecycle test emails enviados para ${response.data.recipient}`);
      }
    } catch (error) {
      console.error('Error testing all lifecycle templates:', error);
      toast.error('Failed to send lifecycle template previews');
    } finally {
      setTestingTemplateId(null);
    }
  };

  const confirmTestAllLifecycleTemplates = () => {
    openConfirmDialog(
      'Testar todos os emails',
      `Enviar 9 emails de teste (em ${templateTestLang === 'en' ? 'inglês' : 'português'}) para ${LIFECYCLE_TEST_RECIPIENT_EMAIL}. Estes são apenas emails de teste — nenhum usuário vai recebê-los e o histórico de Lifecycle não será afetado.`,
      testAllLifecycleTemplates
    );
  };

  // Real-send automation config (master switch + 9 per-event toggles) - backend-safe (Mongo),
  // both default OFF. Dry-run keeps working exactly the same regardless of these toggles.
  const fetchLifecycleAutomation = async () => {
    setLoadingAutomation(true);
    try {
      const response = await api.get('/admin/lifecycle/automation');
      setLifecycleAutomation({
        master_enabled: !!response.data.master_enabled,
        event_enabled: response.data.event_enabled || {}
      });
    } catch (error) {
      console.error('Error fetching lifecycle automation config:', error);
      toast.error('Failed to load lifecycle automation config');
    } finally {
      setLoadingAutomation(false);
    }
  };

  const setMasterAutomation = async (enabled) => {
    setTogglingAutomation('master');
    try {
      const response = await api.put('/admin/lifecycle/automation/master', { enabled });
      setLifecycleAutomation({
        master_enabled: !!response.data.master_enabled,
        event_enabled: response.data.event_enabled || {}
      });
      toast.success(enabled ? 'Automação global de Lifecycle Emails ATIVADA' : 'Automação global de Lifecycle Emails DESATIVADA');
    } catch (error) {
      console.error('Error updating lifecycle automation master switch:', error);
      toast.error('Failed to update automation master switch');
    } finally {
      setTogglingAutomation(null);
    }
  };

  const setEventAutomation = async (templateId, enabled) => {
    setTogglingAutomation(templateId);
    try {
      const response = await api.put(`/admin/lifecycle/automation/event/${templateId}`, { enabled });
      setLifecycleAutomation({
        master_enabled: !!response.data.master_enabled,
        event_enabled: response.data.event_enabled || {}
      });
      const label = templateLabelById[templateId] || templateId;
      toast.success(`${label}: automação ${enabled ? 'ATIVADA' : 'DESATIVADA'}`);
    } catch (error) {
      console.error('Error updating lifecycle automation event toggle:', error);
      toast.error('Failed to update automation event toggle');
    } finally {
      setTogglingAutomation(null);
    }
  };

  const eligibleCountForTemplate = (templateId) => {
    if (templateId === 'monthly_summary') {
      return latestLifecycleRunResults.filter(r => r.event_key?.startsWith('monthly_summary')).length;
    }
    const eventKey = TEMPLATE_ID_TO_EVENT_KEY[templateId];
    return latestLifecycleRunResults.filter(r => r.event_key === eventKey).length;
  };

  const confirmSetMasterAutomation = (enabled) => {
    if (!enabled) {
      setMasterAutomation(false);
      return;
    }
    openConfirmDialog(
      'Enable automatic Lifecycle emails?',
      'Only event types individually enabled below will be sent. User preferences, category unsubscribes, eligibility and dedupe will still be respected.',
      () => setMasterAutomation(true)
    );
  };

  const confirmSetEventAutomation = (templateId, enabled) => {
    if (!enabled) {
      setEventAutomation(templateId, false);
      return;
    }
    const label = templateLabelById[templateId] || templateId;
    const eligible = eligibleCountForTemplate(templateId);
    openConfirmDialog(
      `Enable automatic sending for: ${label}`,
      `Current dry-run eligible users: ${eligible}`,
      () => setEventAutomation(templateId, true)
    );
  };

  // Send test re-engagement email
  const sendTestReengagementEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await api.post('/admin/reengagement/send-test');
      if (response.data?.result?.success) {
        toast.success('Test re-engagement email sent to vinilang13@gmail.com');
      } else {
        toast.error(response.data?.result?.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Send re-engagement email to one selected user (admin picks from Users list)
  const sendIndividualReengagement = (userId, email) => {
    openConfirmDialog(
      'Send Re-engagement Email',
      `Send the re-engagement email to ${email}?`,
      async () => {
        setActionLoading(`reengagement-${userId}`);
        try {
          const response = await api.post('/admin/reengagement/send-individual', { user_id: userId });
          if (response.data?.result?.success) {
            toast.success(`Email sent to ${response.data.sent_to}`);
          } else {
            toast.error(response.data?.result?.error || 'Failed to send email');
          }
        } catch (error) {
          console.error('Error sending individual reengagement email:', error);
          toast.error(error.response?.data?.detail || 'Failed to send email');
        } finally {
          setActionLoading(null);
        }
      }
    );
  };

  // Bulk-send re-engagement email to all eligible inactive free users
  const sendBulkReengagement = async () => {
    setSendingBulkReengagement(true);
    try {
      const dryRun = await api.get('/admin/reengagement/dry-run');
      const eligibleCount = dryRun.data?.eligible_to_receive || 0;

      if (eligibleCount === 0) {
        toast.info('No eligible users to send to right now.');
        setSendingBulkReengagement(false);
        return;
      }

      openConfirmDialog(
        'Send Bulk Re-engagement',
        `Send the re-engagement email to ${eligibleCount} eligible user${eligibleCount === 1 ? '' : 's'}?`,
        async () => {
          setSendingBulkReengagement(true);
          try {
            const response = await api.post('/admin/reengagement/run-campaign');
            const summary = response.data?.summary;
            if (summary) {
              toast.success(`${summary.sent_count} sent${summary.failed_count ? `, ${summary.failed_count} failed` : ''}`);
            } else {
              toast.success('Campaign completed');
            }
          } catch (error) {
            console.error('Error running bulk reengagement campaign:', error);
            toast.error(error.response?.data?.detail || 'Failed to run campaign');
          } finally {
            setSendingBulkReengagement(false);
          }
        }
      );
      setSendingBulkReengagement(false);
    } catch (error) {
      console.error('Error checking reengagement eligibility:', error);
      toast.error('Failed to check eligible users');
      setSendingBulkReengagement(false);
    }
  };

  // Send re-engagement email to a manually selected batch of users (checkbox selection)
  const sendSelectedReengagement = async () => {
    if (selectedUserIds.length === 0) return;
    setSendingSelectedReengagement(true);
    try {
      const check = await api.post('/admin/reengagement/check-eligibility', { user_ids: selectedUserIds });
      const { selected, eligible, ineligible } = check.data;

      if (eligible === 0) {
        toast.info(`None of the ${selected} selected users are currently eligible (cooldown or criteria).`);
        setSendingSelectedReengagement(false);
        return;
      }

      const description = ineligible > 0
        ? `${eligible} of ${selected} selected users are eligible. ${ineligible} will be skipped by current eligibility/cooldown rules.`
        : `${eligible} of ${selected} selected users are eligible.`;

      openConfirmDialog(
        'Send Re-engagement',
        description,
        async () => {
          setSendingSelectedReengagement(true);
          try {
            const response = await api.post('/admin/reengagement/send-selected', { user_ids: selectedUserIds });
            const summary = response.data?.summary;
            if (summary) {
              toast.success(`${summary.sent} sent${summary.failed ? `, ${summary.failed} failed` : ''}${summary.skipped ? `, ${summary.skipped} skipped` : ''}`);
            } else {
              toast.success('Send completed');
            }
            setSelectedUserIds([]);
          } catch (error) {
            console.error('Error sending selected reengagement emails:', error);
            toast.error(error.response?.data?.detail || 'Failed to send emails');
          } finally {
            setSendingSelectedReengagement(false);
          }
        }
      );
      setSendingSelectedReengagement(false);
    } catch (error) {
      console.error('Error checking selected reengagement eligibility:', error);
      toast.error('Failed to check eligibility');
      setSendingSelectedReengagement(false);
    }
  };

  // Filter subscribers by plan
  const filteredSubscribers = useMemo(() => {
    if (subscriberPlanFilter === 'all') return subscribers;
    return subscribers.filter(sub => {
      if (subscriberPlanFilter === 'free') return sub.subscription_status !== 'pro';
      if (subscriberPlanFilter === 'pro') return sub.subscription_status === 'pro';
      return true;
    });
  }, [subscribers, subscriberPlanFilter]);

  // Scope to the latest run only (Phase 2D.1 fix): avoids the counters/table mixing resolved
  // events from older runs, which visually looked like the same user repeating across cycles.
  const latestLifecycleRunResults = useMemo(() => {
    const latestRunId = lifecycleRuns[0]?.id;
    if (!latestRunId) return lifecycleResults;
    return lifecycleResults.filter(r => r.run_id === latestRunId);
  }, [lifecycleResults, lifecycleRuns]);

  // Filter lifecycle dry-run results (latest run only) by event type
  const filteredLifecycleResults = useMemo(() => {
    if (lifecycleEventFilter === 'all') return latestLifecycleRunResults;
    return latestLifecycleRunResults.filter(r => r.event_type === lifecycleEventFilter);
  }, [latestLifecycleRunResults, lifecycleEventFilter]);

  const lifecycleEventCounts = useMemo(() => {
    const counts = { activation: 0, milestone: 0, inactivity: 0, monthly_summary: 0 };
    latestLifecycleRunResults.forEach(r => {
      if (counts[r.event_type] !== undefined) counts[r.event_type] += 1;
    });
    return counts;
  }, [latestLifecycleRunResults]);

  const templateLabelById = useMemo(
    () => Object.fromEntries(lifecycleTemplates.map(t => [t.id, t.label])),
    [lifecycleTemplates]
  );

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const [usersResponse, optionsSummaryResponse] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/options-usage/summary'),
      ]);
      setUsers(usersResponse.data);
      setOptionsUsageSummary(optionsSummaryResponse.data || null);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchOptionsActivityDetail = async (userId, email) => {
    setSelectedOptionsActivity({ user_id: userId, email });
    setLoadingOptionsActivity(true);
    try {
      const response = await api.get(`/admin/options-usage/${userId}`);
      setSelectedOptionsActivity(response.data);
    } catch (error) {
      console.error('Error fetching Options activity:', error);
      toast.error('Não foi possível carregar a atividade do Options');
      setSelectedOptionsActivity(null);
    } finally {
      setLoadingOptionsActivity(false);
    }
  };

  const fetchPayments = async () => {
    setLoadingPayments(true);
    try {
      const response = await api.get('/admin/payments');
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoadingPayments(false);
    }
  };

  const fetchReferrals = async () => {
    setLoadingReferrals(true);
    try {
      const [summaryRes, conversionsRes] = await Promise.all([
        api.get('/admin/referrals/summary'),
        api.get('/admin/referrals/conversions')
      ]);
      setReferralSummary(summaryRes.data.summary || []);
      setReferralTotals(summaryRes.data.totals || null);
      setReferralConversions(conversionsRes.data || []);
    } catch (error) {
      console.error('Error fetching referrals:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoadingReferrals(false);
    }
  };

  const fetchUserUsage = async () => {
    setLoadingUsage(true);
    try {
      const [usageRes, topRes, summaryRes] = await Promise.all([
        api.get('/admin/user-usage'),
        api.get('/admin/user-usage/top-users?limit=10'),
        api.get('/admin/user-usage/project-summary')
      ]);
      setUserUsage(usageRes.data || []);
      setTopUsers(topRes.data || []);
      setProjectSummary(summaryRes.data || null);
    } catch (error) {
      console.error('Error fetching user usage:', error);
      toast.error('Failed to load user usage data');
    } finally {
      setLoadingUsage(false);
    }
  };

  const fetchUserUsageDetail = async (userId) => {
    setLoadingUserDetail(true);
    try {
      const response = await api.get(`/admin/user-usage/${userId}`);
      setSelectedUserUsage(response.data);
    } catch (error) {
      console.error('Error fetching user detail:', error);
      toast.error('Failed to load user details');
    } finally {
      setLoadingUserDetail(false);
    }
  };

  // Copy wallet address with fallback
  const copyWalletAddress = async (wallet) => {
    if (!wallet) return;
    
    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(wallet);
        toast.success("Wallet copied");
        return;
      } catch (err) {
        console.warn("Clipboard API failed, trying fallback:", err);
      }
    }
    
    // Fallback: temporary textarea + execCommand
    try {
      const textArea = document.createElement('textarea');
      textArea.value = wallet;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success("Wallet copied");
        return;
      }
    } catch (err) {
      console.warn("Fallback copy also failed:", err);
    }
    
    toast.error("Could not copy. Please copy manually.");
  };

  // Mark referral conversion as paid
  const markReferralPaid = async (conversionId) => {
    setActionLoading(`referral-${conversionId}`);
    try {
      await api.post(`/admin/referrals/${conversionId}/mark-paid`);
      toast.success("Commission marked as paid");
      fetchReferrals();
    } catch (error) {
      console.error('Error marking referral paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setActionLoading(null);
    }
  };

  // View owed conversions for a specific referrer
  const viewReferrerOwed = async (referrer) => {
    setReferrerDetailsModal({
      open: true,
      referrer: referrer,
      owedConversions: [],
      loading: true
    });
    
    try {
      const response = await api.get(`/admin/referrals/referrer/${referrer.referrer_user_id}/owed`);
      setReferrerDetailsModal(prev => ({
        ...prev,
        owedConversions: response.data.owed_conversions || [],
        totalOwed: response.data.total_owed_usd,
        owedCount: response.data.owed_count,
        walletAddress: response.data.wallet_address,
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching referrer owed:', error);
      toast.error('Failed to load owed conversions');
      setReferrerDetailsModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Mark all owed conversions for a referrer as paid
  const markAllReferrerPaid = async (referrerUserId, referralCode, totalOwed, owedCount) => {
    setConfirmDialog({
      open: true,
      title: 'Confirm Bulk Payout',
      description: `Mark all owed commissions for ${referralCode} as paid?\n\nOwed conversions: ${owedCount}\nTotal payout: $${totalOwed?.toFixed(2)}`,
      variant: 'default',
      action: async () => {
        setActionLoading(`bulk-${referrerUserId}`);
        try {
          const response = await api.post(`/admin/referrals/referrer/${referrerUserId}/mark-all-paid`);
          toast.success(`Marked ${response.data.updated_count} conversions as paid ($${response.data.total_paid_usd})`);
          fetchReferrals();
          setReferrerDetailsModal({ open: false, referrer: null, owedConversions: [], loading: false });
        } catch (error) {
          console.error('Error bulk marking paid:', error);
          toast.error(error.response?.data?.detail || 'Failed to mark as paid');
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  const handleGrantPro = async (userId, planType) => {
    setActionLoading(`grant-${userId}-${planType}`);
    try {
      await api.post(`/admin/users/${userId}/grant-pro?plan_type=${planType}`);
      toast.success(`Pro ${planType} granted successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Error granting pro:', error);
      toast.error(error.response?.data?.detail || 'Failed to grant pro');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetFree = async (userId) => {
    setActionLoading(`free-${userId}`);
    try {
      await api.post(`/admin/users/${userId}/set-free`);
      toast.success('User set to free plan');
      fetchUsers();
    } catch (error) {
      console.error('Error setting free:', error);
      toast.error(error.response?.data?.detail || 'Failed to set free');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    setActionLoading(`delete-${userId}`);
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success(`User ${userEmail} deleted permanently`);
      // Optimistic UI update - remove from list
      setUsers(users.filter(u => u.user_id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleExtend = async (userId, days) => {
    setActionLoading(`extend-${userId}-${days}`);
    try {
      await api.post(`/admin/users/${userId}/extend?days=${days}`);
      toast.success(`Extended by ${days} days`);
      fetchUsers();
    } catch (error) {
      console.error('Error extending:', error);
      toast.error(error.response?.data?.detail || 'Failed to extend');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkPaid = async (paymentId) => {
    setActionLoading(`paid-${paymentId}`);
    try {
      await api.post(`/admin/payments/${paymentId}/mark-paid`);
      toast.success('Payment marked as paid');
      fetchPayments();
      fetchUsers(); // Refresh users to show updated subscription
    } catch (error) {
      console.error('Error marking paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelPayment = async (paymentId) => {
    setActionLoading(`cancel-${paymentId}`);
    try {
      await api.post(`/admin/payments/${paymentId}/cancel`);
      toast.success('Payment cancelled');
      fetchPayments();
    } catch (error) {
      console.error('Error cancelling payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel payment');
    } finally {
      setActionLoading(null);
    }
  };

  const openConfirmDialog = (title, description, action, variant = 'default') => {
    setConfirmDialog({
      open: true,
      title,
      description,
      action,
      variant
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: '',
      description: '',
      action: null,
      variant: 'default'
    });
  };

  const executeConfirmedAction = () => {
    if (confirmDialog.action) {
      confirmDialog.action();
    }
    closeConfirmDialog();
  };

  // Count users by status for filter badges
  const userCounts = useMemo(() => {
    const counts = { all: users.length, free: 0, pro_monthly: 0, pro_yearly: 0 };
    users.forEach(user => {
      if (user.subscription_status === 'pro') {
        if (user.subscription_plan === 'monthly') {
          counts.pro_monthly++;
        } else if (user.subscription_plan === 'yearly') {
          counts.pro_yearly++;
        }
      } else {
        counts.free++;
      }
    });
    return counts;
  }, [users]);

  const sourceCounts = useMemo(() => {
    const counts = { all: users.length, options: 0, other: 0 };
    users.forEach((user) => {
      if (String(user.source || '').toUpperCase() === 'OPTIONS') {
        counts.options += 1;
      } else {
        counts.other += 1;
      }
    });
    return counts;
  }, [users]);

  const usageCounts = useMemo(() => {
    const counts = { all: users.length, options: 0, trades: 0, both: 0, none: 0 };
    users.forEach((user) => {
      const usage = String(user.usage || 'NONE').toUpperCase();
      if (usage === 'OPTIONS') counts.options += 1;
      else if (usage === 'TRADES') counts.trades += 1;
      else if (usage === 'BOTH') counts.both += 1;
      else counts.none += 1;
    });
    return counts;
  }, [users]);

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    let result = [...users];
    
    // 1. Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(user => 
        user.email?.toLowerCase().includes(term) ||
        user.username?.toLowerCase().includes(term) ||
        user.user_id?.toLowerCase().includes(term)
      );
    }
    
    // 2. Status filter
    if (statusFilter !== 'all') {
      result = result.filter(user => {
        if (statusFilter === 'free') {
          return user.subscription_status !== 'pro';
        } else if (statusFilter === 'pro_monthly') {
          return user.subscription_status === 'pro' && user.subscription_plan === 'monthly';
        } else if (statusFilter === 'pro_yearly') {
          return user.subscription_status === 'pro' && user.subscription_plan === 'yearly';
        }
        return true;
      });
    }

    if (sourceFilter !== 'all') {
      result = result.filter((user) => {
        const source = String(user.source || '').toUpperCase();
        if (sourceFilter === 'options') {
          return source === 'OPTIONS';
        }
        return source !== 'OPTIONS';
      });
    }

    if (usageFilter !== 'all') {
      result = result.filter((user) => String(user.usage || 'NONE').toUpperCase() === usageFilter.toUpperCase());
    }
    
    // 3. Expiration sort
    if (expirationSort !== 'none') {
      result.sort((a, b) => {
        const aExp = a.subscription_expires_at ? new Date(a.subscription_expires_at).getTime() : null;
        const bExp = b.subscription_expires_at ? new Date(b.subscription_expires_at).getTime() : null;
        
        // Users without expiration go to the end
        if (aExp === null && bExp === null) return 0;
        if (aExp === null) return 1;
        if (bExp === null) return -1;
        
        if (expirationSort === 'nearest') {
          return aExp - bExp; // Nearest first (ascending)
        } else {
          return bExp - aExp; // Latest first (descending)
        }
      });
    }
    
    return result;
  }, [users, searchTerm, statusFilter, sourceFilter, usageFilter, expirationSort]);

  const isFiltered = searchTerm || statusFilter !== 'all' || sourceFilter !== 'all' || usageFilter !== 'all' || expirationSort !== 'none';

  // Checkbox selection - only acts on currently visible/filtered users
  const filteredUserIds = useMemo(() => filteredUsers.map(u => u.user_id), [filteredUsers]);
  const allVisibleSelected = filteredUserIds.length > 0 && filteredUserIds.every(id => selectedUserIds.includes(id));

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedUserIds(prev => prev.filter(id => !filteredUserIds.includes(id)));
    } else {
      setSelectedUserIds(prev => [...new Set([...prev, ...filteredUserIds])]);
    }
  };


  // Helper to check if payment is within selected period
  const isPaymentInPeriod = (payment) => {
    if (paymentPeriod === 'all') return true;
    
    // Use paid_at for paid payments, fallback to created_at
    const dateStr = payment.status === 'paid' && payment.paid_at ? payment.paid_at : payment.created_at;
    if (!dateStr) return true;
    
    try {
      const paymentDate = new Date(dateStr);
      const now = new Date();
      
      switch (paymentPeriod) {
        case '30d':
          return isAfter(paymentDate, subDays(now, 30));
        case '90d':
          return isAfter(paymentDate, subDays(now, 90));
        case 'year':
          return isAfter(paymentDate, startOfYear(now));
        default:
          return true;
      }
    } catch {
      return true;
    }
  };

  // Filter payments by search term AND time period
  const filteredPayments = useMemo(() => {
    return payments.filter(payment => {
      // Search filter
      const matchesSearch = 
        payment.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.user_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.external_reference?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Period filter
      const matchesPeriod = isPaymentInPeriod(payment);
      
      return matchesSearch && matchesPeriod;
    });
  }, [payments, searchTerm, paymentPeriod]);

  // Calculate revenue summary (only PAID payments within period)
  const revenueSummary = useMemo(() => {
    const paidPayments = filteredPayments.filter(p => p.status === 'paid');
    const totalRevenue = paidPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    return {
      totalRevenue,
      paidCount: paidPayments.length
    };
  }, [filteredPayments]);

  const pendingPayments = filteredPayments.filter(p => p.status === 'pending');
  const otherPayments = filteredPayments.filter(p => p.status !== 'pending');

  // Format USD currency
  const formatUSD = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const getPlanLabel = (user) => {
    if (user?.subscription_status !== 'pro') return 'FREE';
    if (user.subscription_plan === 'monthly') return 'PRO mensal';
    if (user.subscription_plan === 'lifetime') return 'PRO lifetime';
    return 'PRO anual';
  };

  const getSubscriptionBadge = (user) => {
    if (user.subscription_status === 'pro') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 text-[#FFD700] border border-[#FFD700]/30">
          <Crown size={12} weight="fill" />
          PRO {user.subscription_plan?.toUpperCase()}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/5 text-white/60 border border-white/10">
        FREE
      </span>
    );
  };

  const getSourceBadge = (user) => {
    const source = String(user.source || 'UNKNOWN').toUpperCase();
    const styles = {
      OPTIONS: 'bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30',
      TRADES: 'bg-blue-500/15 text-blue-300 border border-blue-500/30',
      ORGANIC: 'bg-white/10 text-white/70 border border-white/10',
      UNKNOWN: 'bg-white/5 text-white/40 border border-white/10',
    };
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold ${styles[source] || styles.UNKNOWN}`}
        data-testid={`user-source-badge-${user.user_id}`}
      >
        {source}
      </span>
    );
  };

  const getUsageBadge = (user) => {
    const usage = String(user.usage || 'NONE').toUpperCase();
    const styles = {
      BOTH: 'bg-emerald-400/15 text-emerald-300 border border-emerald-400/30',
      OPTIONS: 'bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30',
      TRADES: 'bg-blue-500/15 text-blue-300 border border-blue-500/30',
      NONE: 'bg-white/5 text-white/40 border border-white/10',
    };
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold ${styles[usage] || styles.NONE}`}
        data-testid={`user-usage-badge-${user.user_id}`}
      >
        {usage}
      </span>
    );
  };

  const getDefaultProductBadge = (user) => {
    const defaultProduct = String(user.default_product || '').toUpperCase() || 'CURRENT';
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold bg-white/5 text-white/70 border border-white/10"
        data-testid={`user-default-product-badge-${user.user_id}`}
      >
        {defaultProduct}
      </span>
    );
  };

  const buildUserProductSummary = (user) => {
    return [
      `Acquisition: ${String(user.source || 'UNKNOWN').toUpperCase()}`,
      `Default product: ${String(user.default_product || 'current').toUpperCase()}`,
      `Usage: ${String(user.usage || 'NONE').toUpperCase()}`,
      `Last Trades activity: ${formatDateTime(user.trades_last_activity_at)}`,
      `Last Options activity: ${formatDateTime(user.options_last_activity_at)}`,
    ].join('\n');
  };

  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
            <Hourglass size={12} weight="fill" />
            PENDING
          </span>
        );
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30">
            <Check size={12} weight="bold" />
            PAID
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/30">
            <X size={12} weight="bold" />
            CANCELED
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-white/10 text-white/40 border border-white/10">
            EXPIRED
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/5 text-white/60">
            {status?.toUpperCase()}
          </span>
        );
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] p-4 md:p-8" data-testid="admin-page">
      <div className="w-full max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-4xl md:text-5xl font-black font-heading tracking-tight mb-2" data-testid="admin-title">
            Admin Panel
          </h2>
          <p className="text-white/60 text-base font-sans">
            Manage users, subscriptions, and payments
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('users')}
            data-testid="tab-users"
            className={`px-4 py-2 text-sm font-semibold transition-colors rounded-sm flex items-center gap-2 ${
              activeTab === 'users'
                ? "bg-white text-black"
                : "text-white/60 hover:text-white hover:bg-white/5 border border-white/10"
            }`}
          >
            <Users size={18} weight="duotone" />
            Users ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            data-testid="tab-payments"
            className={`px-4 py-2 text-sm font-semibold transition-colors rounded-sm flex items-center gap-2 ${
              activeTab === 'payments'
                ? "bg-white text-black"
                : "text-white/60 hover:text-white hover:bg-white/5 border border-white/10"
            }`}
          >
            <CreditCard size={18} weight="duotone" />
            Payments
            {pendingPayments.length > 0 && (
              <span className="px-1.5 py-0.5 text-xs font-bold rounded bg-yellow-500 text-black">
                {pendingPayments.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('referrals')}
            data-testid="tab-referrals"
            className={`px-4 py-2 text-sm font-semibold transition-colors rounded-sm flex items-center gap-2 ${
              activeTab === 'referrals'
                ? "bg-white text-black"
                : "text-white/60 hover:text-white hover:bg-white/5 border border-white/10"
            }`}
          >
            <ShareNetwork size={18} weight="duotone" />
            Referrals
            {referralTotals?.total_conversions > 0 && (
              <span className="px-1.5 py-0.5 text-xs font-bold rounded bg-[#00E676] text-black">
                {referralTotals.total_conversions}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('usage')}
            data-testid="tab-usage"
            className={`px-4 py-2 text-sm font-semibold transition-colors rounded-sm flex items-center gap-2 ${
              activeTab === 'usage'
                ? "bg-white text-black"
                : "text-white/60 hover:text-white hover:bg-white/5 border border-white/10"
            }`}
          >
            <ChartBar size={18} weight="duotone" />
            User Usage
            {topUsers.some(u => u.high_storage_warning) && (
              <span className="px-1.5 py-0.5 text-xs font-bold rounded bg-red-500 text-white">
                !
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('subscribers')}
            data-testid="tab-subscribers"
            className={`px-4 py-2 text-sm font-semibold transition-colors rounded-sm flex items-center gap-2 ${
              activeTab === 'subscribers'
                ? "bg-white text-black"
                : "text-white/60 hover:text-white hover:bg-white/5 border border-white/10"
            }`}
          >
            <CreditCard size={18} weight="duotone" />
            Product Updates
            {subscriberStats.total > 0 && (
              <span className="px-1.5 py-0.5 text-xs font-bold rounded bg-[#00E676] text-black">
                {subscriberStats.total}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('lifecycle')}
            data-testid="tab-lifecycle"
            className={`px-4 py-2 text-sm font-semibold transition-colors rounded-sm flex items-center gap-2 ${
              activeTab === 'lifecycle'
                ? "bg-white text-black"
                : "text-white/60 hover:text-white hover:bg-white/5 border border-white/10"
            }`}
          >
            <EnvelopeSimple size={18} weight="duotone" />
            Lifecycle Emails
            <span className="px-1.5 py-0.5 text-xs font-bold rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
              DRY-RUN
            </span>
          </button>
          <button
            onClick={() => setActiveTab('outlook')}
            data-testid="tab-outlook"
            className={`px-4 py-2 text-sm font-semibold transition-colors rounded-sm flex items-center gap-2 ${
              activeTab === 'outlook'
                ? "bg-white text-black"
                : "text-white/60 hover:text-white hover:bg-white/5 border border-white/10"
            }`}
          >
            <Compass size={18} weight="duotone" />
            BTC Outlook
          </button>
        </div>

        {/* Admin Tools Section */}
        {activeTab !== 'lifecycle' && (
        <div className="mb-6 flex items-center gap-4">
          <span className="text-white/40 text-sm">Tools:</span>
          <Button
            onClick={sendTestReengagementEmail}
            disabled={sendingTestEmail}
            variant="outline"
            size="sm"
            className="bg-transparent border-white/20 hover:bg-white/5 text-white/80 hover:text-white"
            data-testid="send-test-reengagement-btn"
          >
            {sendingTestEmail ? (
              <>
                <CircleNotch size={14} className="animate-spin mr-2" />
                Sending...
              </>
            ) : (
              <>
                <EnvelopeSimple size={14} className="mr-2" />
                Send test re-engagement email
              </>
            )}
          </Button>
          <span className="text-white/30 text-xs">(vinilang13@gmail.com only)</span>
          <Button
            onClick={sendBulkReengagement}
            disabled={sendingBulkReengagement}
            variant="outline"
            size="sm"
            className="bg-transparent border-white/20 hover:bg-white/5 text-white/80 hover:text-white"
            data-testid="send-bulk-reengagement-btn"
          >
            {sendingBulkReengagement ? (
              <>
                <CircleNotch size={14} className="animate-spin mr-2" />
                Checking...
              </>
            ) : (
              <>
                <EnvelopeSimple size={14} className="mr-2" />
                Send to all eligible
              </>
            )}
          </Button>
        </div>
        )}

        {/* Search - Shared between tabs (hidden on Lifecycle, which has its own event filters) */}
        {activeTab !== 'lifecycle' && (
        <div className="mb-4">
          <div className="relative max-w-md">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              type="text"
              placeholder={activeTab === 'users' ? "Search by email or username..." : "Search by email or reference..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-white/40"
              data-testid="admin-search"
            />
          </div>
        </div>
        )}

        {/* Users Tab Filters - Only show when on Users tab */}
        {activeTab === 'users' && (
          <div className="mb-6 space-y-4">
            {/* Status Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-white/40 text-sm flex items-center gap-1">
                <Funnel size={14} />
                Status:
              </span>
              <button
                onClick={() => setStatusFilter('all')}
                data-testid="filter-all"
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-white text-black'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                All ({userCounts.all})
              </button>
              <button
                onClick={() => setStatusFilter('free')}
                data-testid="filter-free"
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                  statusFilter === 'free'
                    ? 'bg-white/20 text-white border border-white/30'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                Free ({userCounts.free})
              </button>
              <button
                onClick={() => setStatusFilter('pro_monthly')}
                data-testid="filter-pro-monthly"
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors flex items-center gap-1 ${
                  statusFilter === 'pro_monthly'
                    ? 'bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/50'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                <Crown size={12} weight="fill" className={statusFilter === 'pro_monthly' ? 'text-[#FFD700]' : ''} />
                Pro Monthly ({userCounts.pro_monthly})
              </button>
              <button
                onClick={() => setStatusFilter('pro_yearly')}
                data-testid="filter-pro-yearly"
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors flex items-center gap-1 ${
                  statusFilter === 'pro_yearly'
                    ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                <Crown size={12} weight="fill" className={statusFilter === 'pro_yearly' ? 'text-purple-400' : ''} />
                Pro Yearly ({userCounts.pro_yearly})
              </button>
              
              {/* Expiration Sort */}
              <div className="ml-auto flex items-center gap-2">
                <span className="text-white/40 text-sm">Expires:</span>
                <button
                  onClick={() => setExpirationSort(expirationSort === 'nearest' ? 'none' : 'nearest')}
                  data-testid="sort-expires-nearest"
                  className={`px-2 py-1.5 text-xs font-semibold rounded-sm transition-colors flex items-center gap-1 ${
                    expirationSort === 'nearest'
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                  }`}
                  title="Sort by nearest expiration first"
                >
                  <SortAscending size={14} />
                  Nearest
                </button>
                <button
                  onClick={() => setExpirationSort(expirationSort === 'latest' ? 'none' : 'latest')}
                  data-testid="sort-expires-latest"
                  className={`px-2 py-1.5 text-xs font-semibold rounded-sm transition-colors flex items-center gap-1 ${
                    expirationSort === 'latest'
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                  }`}
                  title="Sort by latest expiration first"
                >
                  <SortDescending size={14} />
                  Latest
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-white/40 text-sm">Source:</span>
              <button
                onClick={() => setSourceFilter('all')}
                data-testid="filter-source-all"
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                  sourceFilter === 'all'
                    ? 'bg-white text-black'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                All ({sourceCounts.all})
              </button>
              <button
                onClick={() => setSourceFilter('options')}
                data-testid="filter-source-options"
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                  sourceFilter === 'options'
                    ? 'bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/40'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                Options ({sourceCounts.options})
              </button>
              <button
                onClick={() => setSourceFilter('other')}
                data-testid="filter-source-other"
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                  sourceFilter === 'other'
                    ? 'bg-white/15 text-white border border-white/20'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                Other ({sourceCounts.other})
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-white/40 text-sm">Usage:</span>
              {[
                ['all', 'All', usageCounts.all],
                ['options', 'Options', usageCounts.options],
                ['trades', 'Trades', usageCounts.trades],
                ['both', 'Both', usageCounts.both],
                ['none', 'None', usageCounts.none],
              ].map(([value, label, count]) => (
                <button
                  key={value}
                  onClick={() => setUsageFilter(value)}
                  data-testid={`filter-usage-${value}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                    usageFilter === value
                      ? 'bg-white text-black'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                  }`}
                >
                  {label} ({count})
                </button>
              ))}
            </div>
            
            {/* Filtered results indicator */}
            {isFiltered && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/50">
                  Showing <span className="text-white font-medium">{filteredUsers.length}</span> of {users.length} users
                </span>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setSourceFilter('all');
                    setUsageFilter('all');
                    setExpirationSort('none');
                  }}
                  className="text-white/40 hover:text-white text-xs underline"
                  data-testid="clear-filters"
                >
                  Clear all filters
                </button>
              </div>
            )}

            {/* Selected users action bar */}
            {selectedUserIds.length > 0 && (
              <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-sm px-4 py-2" data-testid="selected-users-bar">
                <span className="text-white/70 text-sm">
                  <span className="text-white font-medium">{selectedUserIds.length}</span> selecionado{selectedUserIds.length === 1 ? '' : 's'}
                </span>
                <Button
                  onClick={sendSelectedReengagement}
                  disabled={sendingSelectedReengagement}
                  size="sm"
                  className="bg-[#00E676]/20 text-[#00E676] hover:bg-[#00E676]/30 text-xs"
                  data-testid="send-selected-reengagement-btn"
                >
                  {sendingSelectedReengagement ? (
                    <>
                      <CircleNotch size={14} className="animate-spin mr-2" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <EnvelopeSimple size={14} className="mr-2" />
                      Enviar Re-engagement
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <section className="mb-5 border border-white/10 bg-[#121212] p-4 rounded-sm" data-testid="options-usage-summary-section">
            <div className="mb-3 flex items-center gap-2">
              <ChartBar size={18} weight="duotone" className="text-[#00E676]" />
              <h2 className="text-sm font-bold uppercase tracking-wide text-white" data-testid="options-usage-summary-title">GENI OPTIONS — USO 30D</h2>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8" data-testid="options-usage-summary-grid">
              {[
                ['active-users', 'Usuários ativos', optionsUsageSummary?.active_users],
                ['sync-users', 'Fizeram Sync', optionsUsageSummary?.sync_users],
                ['dashboard', 'Dashboard', optionsUsageSummary?.feature_counts?.OPTIONS_DASHBOARD_VIEWED],
                ['positions', 'Posições', optionsUsageSummary?.feature_counts?.OPTIONS_OPEN_POSITIONS_VIEWED],
                ['history', 'Histórico', optionsUsageSummary?.feature_counts?.OPTIONS_HISTORY_VIEWED],
                ['performance', 'Performance', optionsUsageSummary?.feature_counts?.OPTIONS_PERFORMANCE_VIEWED],
                ['analytics', 'Análises', optionsUsageSummary?.feature_counts?.OPTIONS_ANALYTICS_VIEWED],
                ['alerts', 'Alertas', optionsUsageSummary?.feature_counts?.OPTIONS_ALERTS_VIEWED],
              ].map(([key, label, value]) => (
                <div key={key} className="min-w-0 border border-white/10 bg-[#0A0A0A] px-3 py-2 rounded-sm" data-testid={`options-usage-summary-${key}`}>
                  <div className="truncate text-xs text-white/40" data-testid={`options-usage-summary-${key}-label`}>{label}</div>
                  <div className="mt-1 font-mono text-lg font-bold text-white" data-testid={`options-usage-summary-${key}-value`}>{value ?? 0}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden" data-testid="users-section">
            {loadingUsers ? (
              <div className="flex items-center justify-center py-12">
                <GeniLoader size="sm" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12 text-white/40">
                {searchTerm ? 'No users match your search' : 'No users found'}
              </div>
            ) : (
              <SyncedScrollTable maxHeight="calc(100vh - 300px)">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 bg-[#0A0A0A]">
                      <th className="text-left px-4 py-3 w-8">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleSelectAllVisible}
                          className="accent-[#00E676] cursor-pointer"
                          data-testid="select-all-users-checkbox"
                          aria-label="Select all visible users"
                        />
                      </th>
                      <th className="text-left px-4 py-3 text-white/60 font-medium">User</th>
                      <th className="text-left px-4 py-3 text-white/60 font-medium">Plano</th>
                      <th className="text-left px-4 py-3 text-white/60 font-medium">Expires</th>
                      <th className="text-left px-4 py-3 text-white/60 font-medium">Role</th>
                      <th className="text-left px-4 py-3 text-white/60 font-medium">Source</th>
                      <th className="text-left px-4 py-3 text-white/60 font-medium">Usage</th>
                      <th className="text-left px-4 py-3 text-white/60 font-medium">Default</th>
                      <th className="text-left px-4 py-3 text-white/60 font-medium">Idioma</th>
                      <th className="text-left px-4 py-3 text-white/60 font-medium">Lifecycle</th>
                      <th className="text-left px-4 py-3 text-white/60 font-medium">Re-engagement</th>
                      <th className="text-right px-4 py-3 text-white/60 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => {
                      // Determine display name with proper fallback
                      const isValidEmail = user.email && user.email !== 'unknown@email.com' && user.email !== 'admin@app.com' && user.email.includes('@');
                      const displayName = isValidEmail 
                        ? user.email 
                        : (user.username || `User ${user.user_id?.slice(0, 8)}...`);
                      const displaySecondary = isValidEmail 
                        ? `ID: ${user.user_id?.slice(0, 8)}...`
                        : (user.email && user.email !== 'unknown@email.com' ? user.email : `Full ID: ${user.user_id}`);
                      const hasEmailIssue = !isValidEmail;
                      
                      return (
                        <tr 
                          key={user.user_id} 
                          className={`border-b border-white/5 hover:bg-white/5 transition-colors ${hasEmailIssue ? 'bg-yellow-500/5' : ''}`}
                          data-testid={`user-row-${user.user_id}`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedUserIds.includes(user.user_id)}
                              onChange={() => toggleUserSelection(user.user_id)}
                              className="accent-[#00E676] cursor-pointer"
                              data-testid={`select-user-${user.user_id}`}
                              aria-label={`Select ${displayName}`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <div className={`font-medium flex items-center gap-2 ${hasEmailIssue ? 'text-yellow-400' : 'text-white'}`}>
                                {displayName}
                                {hasEmailIssue && (
                                  <span className="ml-2 text-xs text-yellow-500/80">(email missing)</span>
                                )}
                                {['OPTIONS', 'BOTH'].includes(String(user.usage || '').toUpperCase()) ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setAdminViewMode(user.user_id, user.email);
                                      navigate('/options');
                                    }}
                                    title="Ver conta como usuário (somente leitura)"
                                    className="flex items-center justify-center rounded-full border border-[#00E676]/30 bg-[#00E676]/10 p-1 text-[#00E676] hover:bg-[#00E676]/20 transition-colors"
                                    data-testid={`admin-view-account-inline-${user.user_id}`}
                                  >
                                    <Eye size={11} />
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => fetchOptionsActivityDetail(user.user_id, user.email)}
                                  title="Ver atividade GENI Options"
                                  className="flex items-center justify-center rounded-full border border-white/15 bg-white/[0.03] p-1 text-white/55 transition-colors hover:bg-white/10 hover:text-white"
                                  data-testid={`admin-options-activity-${user.user_id}`}
                                >
                                  <Clock size={11} />
                                </button>
                              </div>
                              <div className="text-white/40 text-xs truncate max-w-[200px]" title={user.user_id}>
                                {displaySecondary}
                              </div>
                              <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-white/38" data-testid={`user-options-activity-summary-${user.user_id}`}>
                                <span title="Último login" data-testid={`user-last-login-${user.user_id}`}>L {formatDateTime(user.last_login_at)}</span>
                                <span title="Último acesso Options" data-testid={`user-last-options-activity-${user.user_id}`}>O {formatDateTime(user.options_last_activity_at)}</span>
                                <span title="Último Sync" data-testid={`user-last-sync-${user.user_id}`}>S {formatDateTime(user.last_sync_at)}</span>
                              </div>
                            </div>
                          </td>
                        <td className="px-4 py-3">
                          {getSubscriptionBadge(user)}
                        </td>
                        <td className="px-4 py-3 text-white/60">
                          {user.subscription_expires_at ? formatDate(user.subscription_expires_at) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium ${user.role === 'admin' ? 'text-purple-400' : 'text-white/40'}`}>
                            {user.role?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3" title={buildUserProductSummary(user)}>
                          {getSourceBadge(user)}
                        </td>
                        <td className="px-4 py-3" title={buildUserProductSummary(user)}>
                          {getUsageBadge(user)}
                        </td>
                        <td className="px-4 py-3" title={buildUserProductSummary(user)}>
                          {getDefaultProductBadge(user)}
                        </td>
                        <td className="px-4 py-3" data-testid={`locale-badge-${user.user_id}`}>
                          <span
                            className={`text-xs font-mono font-bold ${user.locale_is_fallback ? 'text-white/40' : 'text-white/80'}`}
                            title={user.locale_is_fallback ? 'Idioma padrão/fallback' : 'Idioma salvo pelo usuário'}
                          >
                            {user.locale_effective === 'ptBR' ? 'PT' : 'EN'}{user.locale_is_fallback ? '*' : ''}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-testid={`lifecycle-status-${user.user_id}`}>
                          <span className={`text-xs font-medium ${user.lifecycle_emails_enabled === false ? 'text-white/30' : 'text-[#00E676]'}`}>
                            {user.lifecycle_emails_enabled === false ? 'OFF' : 'ON'}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-testid={`reengagement-count-${user.user_id}`}>
                          {user.reengagement_sent_count > 0 ? (
                            <span
                              className="inline-flex items-center gap-1 text-white/60 text-xs cursor-default"
                              title={`${user.reengagement_sent_count} re-engagement email${user.reengagement_sent_count === 1 ? '' : 's'} sent${user.reengagement_last_sent_at ? `\nLast: ${formatDate(user.reengagement_last_sent_at)}` : ''}`}
                            >
                              <EnvelopeSimple size={12} />
                              {user.reengagement_sent_count}
                            </span>
                          ) : (
                            <span className="text-white/25 text-xs">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            {['OPTIONS', 'BOTH'].includes(String(user.usage || '').toUpperCase()) ? (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setAdminViewMode(user.user_id, user.email);
                                  navigate('/options');
                                }}
                                className="bg-white/10 text-white hover:bg-white/20 text-xs px-2 py-1 h-auto"
                                data-testid={`admin-view-account-${user.user_id}`}
                              >
                                <Eye size={12} className="mr-1" />
                                Ver conta
                              </Button>
                            ) : null}
                            {user.subscription_status !== 'pro' ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => openConfirmDialog(
                                    'Grant Pro Monthly',
                                    `Grant Pro Monthly to ${user.email}?`,
                                    () => handleGrantPro(user.user_id, 'monthly')
                                  )}
                                  disabled={actionLoading?.startsWith(`grant-${user.user_id}`)}
                                  className="bg-[#00E676]/20 text-[#00E676] hover:bg-[#00E676]/30 text-xs px-2 py-1 h-auto"
                                  data-testid={`grant-monthly-${user.user_id}`}
                                >
                                  {actionLoading === `grant-${user.user_id}-monthly` ? (
                                    <CircleNotch size={12} className="animate-spin" />
                                  ) : (
                                    '+ Monthly'
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => openConfirmDialog(
                                    'Grant Pro Yearly',
                                    `Grant Pro Yearly to ${user.email}?`,
                                    () => handleGrantPro(user.user_id, 'yearly')
                                  )}
                                  disabled={actionLoading?.startsWith(`grant-${user.user_id}`)}
                                  className="bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 text-[#FFD700] hover:opacity-80 text-xs px-2 py-1 h-auto"
                                  data-testid={`grant-yearly-${user.user_id}`}
                                >
                                  {actionLoading === `grant-${user.user_id}-yearly` ? (
                                    <CircleNotch size={12} className="animate-spin" />
                                  ) : (
                                    '+ Yearly'
                                  )}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => openConfirmDialog(
                                    'Extend 30 Days',
                                    `Extend ${user.email}'s subscription by 30 days?`,
                                    () => handleExtend(user.user_id, 30)
                                  )}
                                  disabled={actionLoading?.startsWith(`extend-${user.user_id}`)}
                                  className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 text-xs px-2 py-1 h-auto"
                                  data-testid={`extend-30-${user.user_id}`}
                                >
                                  {actionLoading === `extend-${user.user_id}-30` ? (
                                    <CircleNotch size={12} className="animate-spin" />
                                  ) : (
                                    <>
                                      <CalendarPlus size={12} className="mr-1" />
                                      +30d
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => openConfirmDialog(
                                    'Extend 1 Year',
                                    `Extend ${user.email}'s subscription by 1 year?`,
                                    () => handleExtend(user.user_id, 365)
                                  )}
                                  disabled={actionLoading?.startsWith(`extend-${user.user_id}`)}
                                  className="bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 text-xs px-2 py-1 h-auto"
                                  data-testid={`extend-365-${user.user_id}`}
                                >
                                  {actionLoading === `extend-${user.user_id}-365` ? (
                                    <CircleNotch size={12} className="animate-spin" />
                                  ) : (
                                    <>
                                      <CalendarPlus size={12} className="mr-1" />
                                      +1yr
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openConfirmDialog(
                                    'Set to Free',
                                    `Remove Pro status from ${user.email}? This will immediately revoke their access.`,
                                    () => handleSetFree(user.user_id),
                                    'destructive'
                                  )}
                                  disabled={actionLoading === `free-${user.user_id}`}
                                  className="border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10 text-xs px-2 py-1 h-auto"
                                  data-testid={`set-free-${user.user_id}`}
                                >
                                  {actionLoading === `free-${user.user_id}` ? (
                                    <CircleNotch size={12} className="animate-spin" />
                                  ) : (
                                    <>
                                      <Prohibit size={12} className="mr-1" />
                                      Free
                                    </>
                                  )}
                                </Button>
                              </>
                            )}
                            
                            {/* Send Re-engagement Email - Icon only, subtle */}
                            <button
                              onClick={() => sendIndividualReengagement(user.user_id, user.email)}
                              disabled={actionLoading === `reengagement-${user.user_id}` || hasEmailIssue}
                              className="p-1.5 rounded text-white/30 hover:text-[#00E676] hover:bg-[#00E676]/10 transition-colors disabled:opacity-50"
                              data-testid={`send-reengagement-${user.user_id}`}
                              title={hasEmailIssue ? 'No valid email on file' : 'Send re-engagement email'}
                              aria-label="Send re-engagement email"
                            >
                              {actionLoading === `reengagement-${user.user_id}` ? (
                                <CircleNotch size={14} className="animate-spin" />
                              ) : (
                                <EnvelopeSimple size={14} />
                              )}
                            </button>

                            {/* Delete User - Icon only, subtle */}
                            <button
                              onClick={() => openConfirmDialog(
                                'Delete User Permanently',
                                `Are you sure you want to permanently delete ${user.email} and ALL associated data (trades, analytics, alerts, etc.)? This action cannot be undone.`,
                                () => handleDeleteUser(user.user_id, user.email),
                                'destructive'
                              )}
                              disabled={actionLoading === `delete-${user.user_id}`}
                              className="p-1.5 rounded text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                              data-testid={`delete-user-${user.user_id}`}
                              title="Delete user permanently"
                              aria-label="Delete user"
                            >
                              {actionLoading === `delete-${user.user_id}` ? (
                                <CircleNotch size={14} className="animate-spin" />
                              ) : (
                                <Trash size={14} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </SyncedScrollTable>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6" data-testid="payments-section">
            {/* Revenue Summary & Time Filter Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Time Period Filter */}
              <div className="flex items-center gap-2">
                <CalendarBlank size={16} weight="duotone" className="text-white/40" />
                <span className="text-white/60 text-sm">Period:</span>
                <div className="flex gap-1">
                  {[
                    { value: '30d', label: 'Last 30d' },
                    { value: '90d', label: 'Last 90d' },
                    { value: 'year', label: 'This Year' },
                    { value: 'all', label: 'All Time' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setPaymentPeriod(option.value)}
                      data-testid={`period-${option.value}`}
                      className={`px-3 py-1.5 text-xs font-mono rounded-sm border transition-colors ${
                        paymentPeriod === option.value
                          ? 'bg-[#00E676]/10 border-[#00E676]/50 text-[#00E676]'
                          : 'bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20 hover:text-white/80'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Revenue Summary */}
              <div className="flex items-center gap-6 bg-[#121212] border border-white/10 rounded-sm px-5 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-sm bg-[#00E676]/10 flex items-center justify-center">
                    <CurrencyDollar size={18} weight="duotone" className="text-[#00E676]" />
                  </div>
                  <div>
                    <div className="text-white/40 text-[10px] font-mono uppercase">Total Paid</div>
                    <div className="text-lg font-bold font-mono text-[#00E676]" data-testid="total-revenue">
                      {formatUSD(revenueSummary.totalRevenue)}
                    </div>
                  </div>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div>
                  <div className="text-white/40 text-[10px] font-mono uppercase">Transactions</div>
                  <div className="text-lg font-bold font-mono text-white" data-testid="paid-count">
                    {revenueSummary.paidCount}
                  </div>
                </div>
              </div>
            </div>

            {/* Pending Payments */}
            {pendingPayments.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <Hourglass size={20} weight="fill" className="text-yellow-400" />
                  Pending Payments ({pendingPayments.length})
                </h3>
                <div className="bg-[#121212] border border-yellow-500/30 rounded-sm overflow-hidden">
                  <SyncedScrollTable maxHeight="400px">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10 bg-yellow-500/5">
                          <th className="text-left px-4 py-3 text-white/60 font-medium">User</th>
                          <th className="text-left px-4 py-3 text-white/60 font-medium">Plan</th>
                          <th className="text-left px-4 py-3 text-white/60 font-medium">Amount</th>
                          <th className="text-left px-4 py-3 text-white/60 font-medium">Method</th>
                          <th className="text-left px-4 py-3 text-white/60 font-medium">Status</th>
                          <th className="text-left px-4 py-3 text-white/60 font-medium">Reference</th>
                          <th className="text-left px-4 py-3 text-white/60 font-medium">Created</th>
                          <th className="text-right px-4 py-3 text-white/60 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingPayments.map((payment) => (
                          <tr 
                            key={payment.id} 
                            className="border-b border-white/5 hover:bg-white/5 transition-colors"
                            data-testid={`payment-row-${payment.id}`}
                          >
                            <td className="px-4 py-3">
                              {(() => {
                                const isValidEmail = payment.email && payment.email !== 'unknown@email.com' && payment.email.includes('@');
                                const displayName = isValidEmail ? payment.email : `User ${payment.user_id?.slice(0, 8)}...`;
                                const hasEmailIssue = !isValidEmail;
                                return (
                                  <div>
                                    <div className={`font-medium ${hasEmailIssue ? 'text-yellow-400' : 'text-white'}`}>
                                      {displayName}
                                      {hasEmailIssue && <span className="ml-1 text-xs text-yellow-500/80">(!)</span>}
                                    </div>
                                    <div className="text-white/40 text-xs truncate max-w-[150px]" title={payment.user_id}>
                                      ID: {payment.user_id?.slice(0, 8)}...
                                    </div>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-sm font-bold ${payment.plan_type === 'yearly' ? 'text-[#FFD700]' : 'text-white'}`}>
                                {payment.plan_type?.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-white font-mono">
                              {payment.payment_provider === 'crypto_manual' 
                                ? `${payment.crypto_amount || payment.amount} USDT`
                                : `R$${payment.amount?.toFixed(2)}`
                              }
                            </td>
                            <td className="px-4 py-3">
                              {payment.payment_provider === 'crypto_manual' ? (
                                <span className="inline-flex items-center gap-1 text-[#F0B90B] text-xs font-semibold bg-[#F0B90B]/10 px-2 py-1 rounded-sm">
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#F0B90B]"></span>
                                  CRYPTO
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-blue-400 text-xs font-semibold bg-blue-400/10 px-2 py-1 rounded-sm">
                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                                  MP
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1.5 rounded-sm border border-yellow-500/25 bg-yellow-500/10 px-2 py-1 text-xs font-semibold uppercase text-yellow-300" data-testid={`payment-status-${payment.id}`}>
                                <Hourglass size={12} weight="fill" />
                                Pending
                              </span>
                            </td>
                            <td className="px-4 py-3 text-white/60 font-mono text-xs">
                              {payment.external_reference || '-'}
                            </td>
                            <td className="px-4 py-3 text-white/60">
                              {formatDateTime(payment.created_at)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => openConfirmDialog(
                                    'Mark as Paid',
                                    `Confirm payment from ${payment.email} for Pro ${payment.plan_type}? This will grant them Pro access.`,
                                    () => handleMarkPaid(payment.id)
                                  )}
                                  disabled={actionLoading === `paid-${payment.id}`}
                                  className="bg-[#00E676] text-black hover:bg-[#00E676]/80 text-xs px-3 py-1 h-auto font-bold"
                                  data-testid={`mark-paid-${payment.id}`}
                                >
                                  {actionLoading === `paid-${payment.id}` ? (
                                    <CircleNotch size={12} className="animate-spin" />
                                  ) : (
                                    <>
                                      <Check size={14} weight="bold" className="mr-1" />
                                      Mark Paid
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openConfirmDialog(
                                    'Cancel Payment',
                                    `Cancel this payment request from ${payment.email}?`,
                                    () => handleCancelPayment(payment.id),
                                    'destructive'
                                  )}
                                  disabled={actionLoading === `cancel-${payment.id}`}
                                  className="border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10 text-xs px-2 py-1 h-auto"
                                  data-testid={`cancel-payment-${payment.id}`}
                                >
                                  {actionLoading === `cancel-${payment.id}` ? (
                                    <CircleNotch size={12} className="animate-spin" />
                                  ) : (
                                    <X size={14} weight="bold" />
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </SyncedScrollTable>
                </div>
              </div>
            )}

            {/* All Payments History */}
            <div>
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <CreditCard size={20} weight="duotone" />
                Payment History
              </h3>
              <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                {loadingPayments ? (
                  <div className="flex items-center justify-center py-12">
                    <CircleNotch size={32} className="animate-spin text-white/40" />
                  </div>
                ) : filteredPayments.length === 0 ? (
                  <div className="text-center py-12 text-white/40">
                    {searchTerm ? 'No payments match your search' : 'No payments yet'}
                  </div>
                ) : (
                  <SyncedScrollTable maxHeight="calc(100vh - 400px)">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10 bg-[#0A0A0A]">
                          <th className="text-left px-4 py-3 text-white/60 font-medium">Status</th>
                          <th className="text-left px-4 py-3 text-white/60 font-medium">User</th>
                          <th className="text-left px-4 py-3 text-white/60 font-medium">Plan</th>
                          <th className="text-left px-4 py-3 text-white/60 font-medium">Amount</th>
                          <th className="text-left px-4 py-3 text-white/60 font-medium">Reference</th>
                          <th className="text-left px-4 py-3 text-white/60 font-medium">Created</th>
                          <th className="text-left px-4 py-3 text-white/60 font-medium">Paid At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPayments.map((payment) => {
                          const isValidEmail = payment.email && payment.email !== 'unknown@email.com' && payment.email.includes('@');
                          const displayName = isValidEmail ? payment.email : `User ${payment.user_id?.slice(0, 8)}...`;
                          const hasEmailIssue = !isValidEmail;
                          
                          return (
                            <tr 
                              key={payment.id} 
                              className={`border-b border-white/5 hover:bg-white/5 transition-colors ${hasEmailIssue ? 'bg-yellow-500/5' : ''}`}
                            >
                              <td className="px-4 py-3">
                                {getPaymentStatusBadge(payment.status)}
                              </td>
                              <td className="px-4 py-3">
                                <div className={hasEmailIssue ? 'text-yellow-400' : 'text-white'}>
                                  {displayName}
                                  {hasEmailIssue && <span className="ml-1 text-xs text-yellow-500/80">(!)</span>}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-sm font-bold ${payment.plan_type === 'yearly' ? 'text-[#FFD700]' : 'text-white'}`}>
                                  {payment.plan_type?.toUpperCase()}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-white font-mono">
                                R${payment.amount?.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-white/60 font-mono text-xs">
                                {payment.external_reference || '-'}
                              </td>
                              <td className="px-4 py-3 text-white/60">
                                {formatDateTime(payment.created_at)}
                              </td>
                              <td className="px-4 py-3 text-white/60">
                                {payment.paid_at ? formatDateTime(payment.paid_at) : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </SyncedScrollTable>
                )}
              </div>
            </div>

            {/* Refresh Button */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  fetchPayments();
                  fetchUsers();
                }}
                className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                data-testid="refresh-data"
              >
                <ArrowClockwise size={16} className="mr-2" />
                Refresh Data
              </Button>
            </div>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-6" data-testid="referrals-tab">
            {/* Summary Cards */}
            {referralTotals && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={18} className="text-white/40" />
                    <span className="text-white/40 text-xs font-mono">REFERRERS</span>
                  </div>
                  <div className="text-2xl font-bold font-mono text-white">{referralTotals.total_referrers}</div>
                </div>
                <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ShareNetwork size={18} className="text-white/40" />
                    <span className="text-white/40 text-xs font-mono">CONVERSIONS</span>
                  </div>
                  <div className="text-2xl font-bold font-mono text-[#00E676]">{referralTotals.total_conversions}</div>
                </div>
                <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CurrencyDollar size={18} className="text-white/40" />
                    <span className="text-white/40 text-xs font-mono">REVENUE</span>
                  </div>
                  <div className="text-2xl font-bold font-mono text-white">${referralTotals.total_revenue_usd?.toFixed(2)}</div>
                </div>
                <div className="bg-[#121212] border border-[#FFC107]/30 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Hourglass size={18} className="text-[#FFC107]" />
                    <span className="text-white/40 text-xs font-mono">COMM. OWED</span>
                  </div>
                  <div className="text-2xl font-bold font-mono text-[#FFC107]">${referralTotals.commission_owed_usd?.toFixed(2) || '0.00'}</div>
                </div>
                <div className="bg-[#121212] border border-[#00E676]/30 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={18} className="text-[#00E676]" />
                    <span className="text-white/40 text-xs font-mono">COMM. PAID</span>
                  </div>
                  <div className="text-2xl font-bold font-mono text-[#00E676]">${referralTotals.commission_paid_usd?.toFixed(2) || '0.00'}</div>
                </div>
              </div>
            )}

            {/* View Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setReferralView('summary')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                  referralView === 'summary'
                    ? "bg-white/10 text-white border border-white/20"
                    : "text-white/60 hover:text-white border border-white/10"
                }`}
              >
                By Referrer
              </button>
              <button
                onClick={() => setReferralView('conversions')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                  referralView === 'conversions'
                    ? "bg-white/10 text-white border border-white/20"
                    : "text-white/60 hover:text-white border border-white/10"
                }`}
              >
                All Conversions
              </button>
            </div>

            {loadingReferrals ? (
              <div className="flex items-center justify-center py-12">
                <CircleNotch size={32} className="animate-spin text-white/40" />
              </div>
            ) : referralView === 'summary' ? (
              /* Referrers Summary Table */
              <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                {referralSummary.length === 0 ? (
                  <div className="p-8 text-center">
                    <ShareNetwork size={32} className="mx-auto mb-3 text-white/20" />
                    <p className="text-white/40">No referral conversions yet</p>
                  </div>
                ) : (
                  <SyncedScrollTable maxHeight="calc(100vh - 400px)">
                    <table className="w-full" data-testid="referrers-table">
                      <thead className="bg-[#0A0A0A]">
                        <tr className="text-left text-xs text-white/40 font-mono border-b border-white/10">
                          <th className="px-4 py-3">REFERRER</th>
                          <th className="px-4 py-3">CODE</th>
                          <th className="px-4 py-3">CONV.</th>
                          <th className="px-4 py-3">M/Y</th>
                          <th className="px-4 py-3">REVENUE</th>
                          <th className="px-4 py-3">OWED</th>
                          <th className="px-4 py-3">PAID</th>
                          <th className="px-4 py-3">WALLET</th>
                          <th className="px-4 py-3">ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referralSummary.map((ref, idx) => (
                          <tr key={idx} className="border-t border-white/10 hover:bg-white/5">
                            <td className="px-4 py-3">
                              <div className="text-white text-sm">{ref.referrer_email}</div>
                              <div className="text-white/40 text-xs font-mono">{ref.referrer_user_id?.slice(0, 8)}...</div>
                            </td>
                            <td className="px-4 py-3 font-mono text-[#00E676] font-bold">{ref.referral_code}</td>
                            <td className="px-4 py-3 font-mono text-white font-bold">{ref.total_conversions}</td>
                            <td className="px-4 py-3 font-mono text-white/60 text-sm">{ref.monthly_count}/{ref.yearly_count}</td>
                            <td className="px-4 py-3 font-mono text-white">${ref.total_revenue_usd?.toFixed(2)}</td>
                            <td className="px-4 py-3 font-mono text-[#FFC107] font-bold">${ref.commission_owed_usd?.toFixed(2) || '0.00'}</td>
                            <td className="px-4 py-3 font-mono text-[#00E676]">${ref.commission_paid_usd?.toFixed(2) || '0.00'}</td>
                            <td className="px-4 py-3">
                              {ref.wallet_address ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-mono text-white/60" title={ref.wallet_address}>
                                    {ref.wallet_address.slice(0, 6)}...{ref.wallet_address.slice(-4)}
                                  </span>
                                  <button
                                    onClick={() => copyWalletAddress(ref.wallet_address)}
                                    className="p-1 text-white/40 hover:text-white hover:bg-white/10 rounded transition-colors"
                                    title="Copy wallet address"
                                  >
                                    <Copy size={14} />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-white/30">Not set</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {ref.commission_owed_usd > 0 ? (
                                <button
                                  onClick={() => viewReferrerOwed(ref)}
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-[#FFC107]/10 text-[#FFC107] hover:bg-[#FFC107]/20 transition-colors"
                                  title="View owed conversions"
                                >
                                  <Wallet size={12} />
                                  View Owed
                                </button>
                              ) : (
                                <span className="text-xs text-white/30">All paid</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </SyncedScrollTable>
                )}
              </div>
            ) : (
              /* Conversions Table */
              <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                {referralConversions.length === 0 ? (
                  <div className="p-8 text-center">
                    <ShareNetwork size={32} className="mx-auto mb-3 text-white/20" />
                    <p className="text-white/40">No referral conversions yet</p>
                  </div>
                ) : (
                  <SyncedScrollTable maxHeight="calc(100vh - 400px)">
                    <table className="w-full" data-testid="conversions-table">
                      <thead className="bg-[#0A0A0A]">
                        <tr className="text-left text-xs text-white/40 font-mono border-b border-white/10">
                          <th className="px-4 py-3">REFERRER</th>
                          <th className="px-4 py-3">REFERRED USER</th>
                          <th className="px-4 py-3">PLAN</th>
                          <th className="px-4 py-3">PAYMENT</th>
                          <th className="px-4 py-3">REWARD</th>
                          <th className="px-4 py-3">STATUS</th>
                          <th className="px-4 py-3">WALLET</th>
                          <th className="px-4 py-3">ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referralConversions.map((conv, idx) => (
                          <tr key={idx} className="border-t border-white/10 hover:bg-white/5">
                            <td className="px-4 py-3">
                              <div className="text-white text-sm">{conv.referrer_email}</div>
                              <div className="text-[#00E676] text-xs font-mono">{conv.referrer_code}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-white text-sm">{conv.referred_email}</div>
                              <div className="text-white/40 text-xs font-mono">{conv.referred_user_id?.slice(0, 8)}...</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                                conv.plan_type === 'yearly' 
                                  ? 'bg-[#FFC107]/20 text-[#FFC107]' 
                                  : 'bg-white/10 text-white/70'
                              }`}>
                                {conv.plan_type?.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-mono text-white">${conv.payment_amount_usd?.toFixed(2)}</td>
                            <td className="px-4 py-3 font-mono text-[#00E676] font-bold">${conv.reward_amount_usd?.toFixed(2)}</td>
                            <td className="px-4 py-3">
                              {conv.payout_status === 'paid' ? (
                                <div>
                                  <span className="px-2 py-0.5 text-xs font-bold rounded bg-[#00E676]/20 text-[#00E676]">
                                    PAID
                                  </span>
                                  {conv.paid_at && (
                                    <div className="text-white/40 text-xs mt-1">{formatDateTime(conv.paid_at)}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="px-2 py-0.5 text-xs font-bold rounded bg-[#FFC107]/20 text-[#FFC107]">
                                  OWED
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {conv.wallet_address ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-mono text-white/60" title={conv.wallet_address}>
                                    {conv.wallet_address.slice(0, 6)}...{conv.wallet_address.slice(-4)}
                                  </span>
                                  <button
                                    onClick={() => copyWalletAddress(conv.wallet_address)}
                                    className="p-1 text-white/40 hover:text-white hover:bg-white/10 rounded transition-colors"
                                    title="Copy wallet address"
                                  >
                                    <Copy size={14} />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-white/30">Not set</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {conv.payout_status !== 'paid' && (
                                <button
                                  onClick={() => markReferralPaid(conv.id)}
                                  disabled={actionLoading === `referral-${conv.id}`}
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-[#00E676]/10 text-[#00E676] hover:bg-[#00E676]/20 transition-colors disabled:opacity-50"
                                  title="Mark commission as paid"
                                >
                                  {actionLoading === `referral-${conv.id}` ? (
                                    <CircleNotch size={12} className="animate-spin" />
                                  ) : (
                                    <Check size={12} weight="bold" />
                                  )}
                                  Mark Paid
                                </button>
                              )}
                              {conv.payout_status === 'paid' && (
                                <span className="text-xs text-white/30">Completed</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </SyncedScrollTable>
                )}
              </div>
            )}

            {/* Refresh Button */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={fetchReferrals}
                className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
              >
                <ArrowClockwise size={16} className="mr-2" />
                Refresh Data
              </Button>
            </div>
          </div>
        )}

        {/* User Usage Tab */}
        {activeTab === 'usage' && (
          <div className="space-y-6" data-testid="usage-tab">
            
            {/* Disclaimer Banner */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm px-4 py-2 flex items-center gap-2">
              <Database size={16} className="text-blue-400" />
              <span className="text-blue-300 text-sm">Internal estimate only — not exact Supabase billing</span>
            </div>

            {/* Project Storage Summary */}
            {projectSummary && (
              <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Database size={20} className="text-[#00E676]" />
                    <h3 className="text-lg font-bold text-white">Project Storage Summary</h3>
                  </div>
                  <span className="text-white/40 text-xs">
                    Snapshot: {projectSummary.snapshot_at ? new Date(projectSummary.snapshot_at).toLocaleString() : '-'}
                  </span>
                </div>

                {/* Totals Row */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                    <div className="text-white/40 text-xs mb-1">Total Users</div>
                    <div className="text-2xl font-bold font-mono text-white">{projectSummary.totals?.total_users || 0}</div>
                  </div>
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                    <div className="text-white/40 text-xs mb-1">Total Images</div>
                    <div className="text-2xl font-bold font-mono text-white">{projectSummary.totals?.total_images || 0}</div>
                  </div>
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                    <div className="text-white/40 text-xs mb-1">Image Storage</div>
                    <div className="text-xl font-bold font-mono text-white">{projectSummary.totals?.total_image_storage_mb || 0} MB</div>
                  </div>
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                    <div className="text-white/40 text-xs mb-1">DB Storage</div>
                    <div className="text-xl font-bold font-mono text-white">{projectSummary.totals?.total_db_storage_mb || 0} MB</div>
                  </div>
                  <div className="bg-gradient-to-br from-[#00E676]/10 to-[#00E676]/5 border border-[#00E676]/30 rounded-sm p-3">
                    <div className="text-[#00E676]/70 text-xs mb-1">Total Storage</div>
                    <div className="text-xl font-bold font-mono text-[#00E676]">
                      {projectSummary.totals?.total_storage_mb || 0} MB
                      <span className="text-sm text-[#00E676]/60 ml-1">
                        ({projectSummary.totals?.total_storage_gb || 0} GB)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Averages & Growth Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                    <div className="text-white/40 text-xs mb-1">Avg Storage / User</div>
                    <div className="text-lg font-bold font-mono text-white">{projectSummary.averages?.avg_storage_per_user_mb || 0} MB</div>
                  </div>
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                    <div className="text-white/40 text-xs mb-1">Avg Images / User</div>
                    <div className="text-lg font-bold font-mono text-white">{projectSummary.averages?.avg_images_per_user || 0}</div>
                  </div>
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                    <div className="text-white/40 text-xs mb-1">Avg Image Size</div>
                    <div className="text-lg font-bold font-mono text-white">{projectSummary.averages?.avg_image_size_kb || 0} KB</div>
                  </div>
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                    <div className="text-white/40 text-xs mb-1">Daily Avg Growth</div>
                    <div className="text-lg font-bold font-mono text-white">{projectSummary.growth?.daily_avg_growth_mb || 0} MB</div>
                  </div>
                </div>

                {/* Growth Indicators */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 flex items-center justify-between">
                    <div>
                      <div className="text-white/40 text-xs">7-Day Growth</div>
                      <div className="text-white text-sm">Storage added in last 7 days</div>
                    </div>
                    <div className="text-xl font-bold font-mono text-yellow-400">
                      +{projectSummary.growth?.storage_added_7d_mb || 0} MB
                    </div>
                  </div>
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 flex items-center justify-between">
                    <div>
                      <div className="text-white/40 text-xs">30-Day Growth</div>
                      <div className="text-white text-sm">Storage added in last 30 days</div>
                    </div>
                    <div className="text-xl font-bold font-mono text-yellow-400">
                      +{projectSummary.growth?.storage_added_30d_mb || 0} MB
                    </div>
                  </div>
                </div>

                {/* Projections */}
                <div className="mb-4">
                  <div className="text-white/60 text-sm mb-2">Projected Storage at Scale (based on current avg)</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 text-center">
                      <div className="text-white/40 text-xs mb-1">100 Users</div>
                      <div className="text-lg font-bold font-mono text-white">
                        {projectSummary.projections?.at_100_users_mb || 0} MB
                      </div>
                      <div className="text-white/40 text-xs">{projectSummary.projections?.at_100_users_gb || 0} GB</div>
                    </div>
                    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 text-center">
                      <div className="text-white/40 text-xs mb-1">500 Users</div>
                      <div className="text-lg font-bold font-mono text-white">
                        {projectSummary.projections?.at_500_users_mb || 0} MB
                      </div>
                      <div className="text-white/40 text-xs">{projectSummary.projections?.at_500_users_gb || 0} GB</div>
                    </div>
                    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 text-center">
                      <div className="text-white/40 text-xs mb-1">1000 Users</div>
                      <div className="text-lg font-bold font-mono text-white">
                        {projectSummary.projections?.at_1000_users_mb || 0} MB
                      </div>
                      <div className="text-white/40 text-xs">{projectSummary.projections?.at_1000_users_gb || 0} GB</div>
                    </div>
                  </div>
                </div>

                {/* Users with High Avg Image Size */}
                {projectSummary.warnings?.users_with_high_avg_image_size?.length > 0 && (
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-sm p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Warning size={16} className="text-orange-400" />
                      <span className="text-orange-400 font-semibold text-sm">Users with High Avg Image Size (&gt;500KB)</span>
                    </div>
                    <div className="space-y-1">
                      {projectSummary.warnings.users_with_high_avg_image_size.map((user, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-white/70">{user.email}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-white/40">{user.total_images} images</span>
                            <span className="text-orange-400 font-mono">{user.avg_image_size_kb} KB avg</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Top Heavy Users Section */}
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Warning size={20} className="text-yellow-500" />
                  <h3 className="text-lg font-bold text-white">Top Heaviest Users</h3>
                </div>
                <span className="text-white/40 text-xs">By estimated storage</span>
              </div>
              
              {loadingUsage ? (
                <div className="flex items-center justify-center py-8">
                  <CircleNotch size={32} className="animate-spin text-white/40" />
                </div>
              ) : (
                <div className="space-y-2">
                  {topUsers.map((user, idx) => (
                    <div 
                      key={user.user_id}
                      className={`flex items-center justify-between p-3 rounded-sm border ${
                        user.high_storage_warning 
                          ? 'bg-red-500/10 border-red-500/30' 
                          : 'bg-[#0A0A0A] border-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-white/40 font-mono text-sm w-6">#{idx + 1}</span>
                        <div>
                          <div className="text-white text-sm">{user.email}</div>
                          <div className="flex items-center gap-3 text-xs text-white/40">
                            <span>{user.total_trades} trades</span>
                            <span>{user.total_images} images</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className={`font-mono font-bold ${
                            user.high_storage_warning ? 'text-red-400' : 'text-white'
                          }`}>
                            {user.total_storage_kb >= 1024 
                              ? `${(user.total_storage_kb / 1024).toFixed(1)} MB`
                              : `${user.total_storage_kb.toFixed(0)} KB`
                            }
                          </div>
                          {user.high_storage_warning && (
                            <span className="text-xs text-red-400">High usage</span>
                          )}
                        </div>
                        <button
                          onClick={() => fetchUserUsageDetail(user.user_id)}
                          className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded transition-colors"
                          title="View details"
                        >
                          <Eye size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Search and Filter */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input
                  type="text"
                  placeholder="Search by email..."
                  value={usageSearchTerm}
                  onChange={(e) => setUsageSearchTerm(e.target.value)}
                  className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-white/40"
                />
              </div>
              <div className="flex items-center gap-2">
                <Funnel size={16} className="text-white/40" />
                {['all', 'light', 'medium', 'heavy'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setUsageActivityFilter(filter)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                      usageActivityFilter === filter
                        ? filter === 'heavy' 
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : filter === 'medium'
                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                          : filter === 'light'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-white text-black'
                        : 'text-white/60 hover:text-white hover:bg-white/5 border border-white/10'
                    }`}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* All Users Usage Table */}
            <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 bg-[#0A0A0A]">
                <h3 className="text-white font-semibold">All Users Usage</h3>
              </div>
              
              {loadingUsage ? (
                <div className="flex items-center justify-center py-12">
                  <CircleNotch size={32} className="animate-spin text-white/40" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 text-left">
                        <th className="px-4 py-3 text-xs font-semibold text-white/60">User</th>
                        <th className="px-4 py-3 text-xs font-semibold text-white/60 text-center">Status</th>
                        <th className="px-4 py-3 text-xs font-semibold text-white/60 text-center">Trades</th>
                        <th className="px-4 py-3 text-xs font-semibold text-white/60 text-center">Open</th>
                        <th className="px-4 py-3 text-xs font-semibold text-white/60 text-center">Planned</th>
                        <th className="px-4 py-3 text-xs font-semibold text-white/60 text-center">Images</th>
                        <th className="px-4 py-3 text-xs font-semibold text-white/60 text-center">Avg Img</th>
                        <th className="px-4 py-3 text-xs font-semibold text-white/60 text-right">Storage</th>
                        <th className="px-4 py-3 text-xs font-semibold text-white/60 text-center">Activity</th>
                        <th className="px-4 py-3 text-xs font-semibold text-white/60 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userUsage
                        .filter(u => {
                          const matchesSearch = u.email?.toLowerCase().includes(usageSearchTerm.toLowerCase());
                          const matchesFilter = usageActivityFilter === 'all' || u.activity_score === usageActivityFilter;
                          return matchesSearch && matchesFilter;
                        })
                        .map((user) => (
                          <tr 
                            key={user.user_id} 
                            className={`border-b border-white/5 hover:bg-white/5 ${
                              user.high_storage_warning ? 'bg-red-500/5' : user.high_avg_image_warning ? 'bg-orange-500/5' : ''
                            }`}
                          >
                            <td className="px-4 py-3">
                              <div className="text-white text-sm">{user.email}</div>
                              <div className="text-white/40 text-xs font-mono">{user.user_id.slice(0, 8)}...</div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${
                                user.subscription_status === 'pro'
                                  ? 'bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 text-[#FFD700] border border-[#FFD700]/30'
                                  : 'bg-white/5 text-white/60 border border-white/10'
                              }`}>
                                {user.subscription_status === 'pro' && <Crown size={12} weight="fill" />}
                                {user.subscription_status?.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center text-white font-mono">{user.total_trades}</td>
                            <td className="px-4 py-3 text-center text-white/70 font-mono">{user.open_positions}</td>
                            <td className="px-4 py-3 text-center text-white/70 font-mono">{user.planned_trades}</td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Image size={14} className="text-white/40" />
                                <span className="text-white/70 font-mono">{user.total_images}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-mono text-sm ${user.high_avg_image_warning ? 'text-orange-400' : 'text-white/70'}`}>
                                {user.avg_image_size_kb > 0 ? `${Math.round(user.avg_image_size_kb)} KB` : '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className={`font-mono text-sm ${user.high_storage_warning ? 'text-red-400' : 'text-white'}`}>
                                {user.total_storage_kb >= 1024 
                                  ? `${(user.total_storage_kb / 1024).toFixed(1)} MB`
                                  : `${user.total_storage_kb.toFixed(0)} KB`
                                }
                              </div>
                              <div className="text-xs text-white/40">
                                {user.estimated_image_storage_kb >= 1024 
                                  ? `${(user.estimated_image_storage_kb / 1024).toFixed(1)} MB img`
                                  : `${user.estimated_image_storage_kb.toFixed(0)} KB img`
                                }
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                                user.activity_score === 'heavy'
                                  ? 'bg-red-500/20 text-red-400'
                                  : user.activity_score === 'medium'
                                  ? 'bg-yellow-500/20 text-yellow-400'
                                  : 'bg-green-500/20 text-green-400'
                              }`}>
                                {user.activity_score?.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => fetchUserUsageDetail(user.user_id)}
                                className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded transition-colors"
                                title="View details"
                              >
                                <Eye size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Refresh Button */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={fetchUserUsage}
                className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
              >
                <ArrowClockwise size={16} className="mr-2" />
                Refresh Data
              </Button>
            </div>
          </div>
        )}

        {/* User Usage Detail Modal */}
        {selectedUserUsage && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedUserUsage(null);
            }}
          >
            <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-2xl max-h-[80vh] overflow-hidden">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold text-lg">User Usage Breakdown</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-white/60 text-sm">{selectedUserUsage.email}</span>
                    <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                      selectedUserUsage.activity_score === 'heavy'
                        ? 'bg-red-500/20 text-red-400'
                        : selectedUserUsage.activity_score === 'medium'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {selectedUserUsage.activity_score?.toUpperCase()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedUserUsage(null)}
                  className="text-white/40 hover:text-white p-1"
                >
                  <X size={20} weight="bold" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
                {loadingUserDetail ? (
                  <div className="flex items-center justify-center py-8">
                    <CircleNotch size={32} className="animate-spin text-white/40" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                        <div className="text-white/40 text-xs mb-1">Total Trades</div>
                        <div className="text-2xl font-bold font-mono text-white">
                          {selectedUserUsage.summary?.total_trades || 0}
                        </div>
                      </div>
                      <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                        <div className="text-white/40 text-xs mb-1">Images</div>
                        <div className="text-2xl font-bold font-mono text-white">
                          {selectedUserUsage.summary?.total_images || 0}
                        </div>
                      </div>
                      <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                        <div className="text-white/40 text-xs mb-1">Image Storage</div>
                        <div className="text-xl font-bold font-mono text-white">
                          {selectedUserUsage.summary?.estimated_image_storage_kb >= 1024 
                            ? `${(selectedUserUsage.summary.estimated_image_storage_kb / 1024).toFixed(1)} MB`
                            : `${selectedUserUsage.summary?.estimated_image_storage_kb?.toFixed(0) || 0} KB`
                          }
                        </div>
                      </div>
                      <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                        <div className="text-white/40 text-xs mb-1">Total Storage</div>
                        <div className={`text-xl font-bold font-mono ${
                          selectedUserUsage.high_storage_warning ? 'text-red-400' : 'text-white'
                        }`}>
                          {selectedUserUsage.summary?.total_storage_kb >= 1024 
                            ? `${(selectedUserUsage.summary.total_storage_kb / 1024).toFixed(1)} MB`
                            : `${selectedUserUsage.summary?.total_storage_kb?.toFixed(0) || 0} KB`
                          }
                        </div>
                      </div>
                    </div>

                    {/* High Storage Warning */}
                    {selectedUserUsage.high_storage_warning && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 flex items-center gap-3">
                        <Warning size={24} className="text-red-400" />
                        <div>
                          <div className="text-red-400 font-semibold">High Storage Warning</div>
                          <div className="text-red-400/70 text-sm">This user exceeds 50MB of estimated storage</div>
                        </div>
                      </div>
                    )}

                    {/* Trades Breakdown */}
                    <div>
                      <h4 className="text-white font-semibold mb-3">Trades Breakdown</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs mb-2">By Status</div>
                          <div className="space-y-1">
                            {Object.entries(selectedUserUsage.trades_breakdown?.by_status || {}).map(([status, count]) => (
                              <div key={status} className="flex justify-between text-sm">
                                <span className="text-white/60 capitalize">{status}</span>
                                <span className="text-white font-mono">{count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs mb-2">By Outcome</div>
                          <div className="space-y-1">
                            {Object.entries(selectedUserUsage.trades_breakdown?.by_outcome || {}).map(([outcome, count]) => (
                              <div key={outcome} className="flex justify-between text-sm">
                                <span className={`capitalize ${
                                  outcome === 'win' ? 'text-green-400' 
                                  : outcome === 'loss' ? 'text-red-400' 
                                  : 'text-white/60'
                                }`}>{outcome}</span>
                                <span className="text-white font-mono">{count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Monthly Activity */}
                    {selectedUserUsage.trades_breakdown?.by_month && 
                     Object.keys(selectedUserUsage.trades_breakdown.by_month).length > 0 && (
                      <div>
                        <h4 className="text-white font-semibold mb-3">Monthly Activity</h4>
                        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                          <div className="space-y-1">
                            {Object.entries(selectedUserUsage.trades_breakdown.by_month)
                              .slice(0, 6)
                              .map(([month, count]) => (
                                <div key={month} className="flex justify-between text-sm">
                                  <span className="text-white/60">{month}</span>
                                  <span className="text-white font-mono">{count} trades</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Setups & Asset Groups */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-white font-semibold mb-2">
                          Setups ({selectedUserUsage.setups?.length || 0})
                        </h4>
                        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 max-h-32 overflow-y-auto">
                          {selectedUserUsage.setups?.length > 0 ? (
                            <div className="space-y-1">
                              {selectedUserUsage.setups.map((setup) => (
                                <div key={setup.id} className="text-sm text-white/70">{setup.name}</div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-white/40 text-sm">No custom setups</div>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-2">
                          Asset Groups ({selectedUserUsage.asset_groups?.length || 0})
                        </h4>
                        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 max-h-32 overflow-y-auto">
                          {selectedUserUsage.asset_groups?.length > 0 ? (
                            <div className="space-y-1">
                              {selectedUserUsage.asset_groups.map((group) => (
                                <div key={group.id} className="flex items-center justify-between text-sm">
                                  <span className="text-white/70">{group.name}</span>
                                  <span className="text-white/40 text-xs">{group.type}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-white/40 text-sm">No asset groups</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                      <div className="text-white/40 text-xs mb-2">User Info</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-white/40">User ID:</span>
                          <span className="text-white/70 font-mono ml-2">{selectedUserUsage.user_id}</span>
                        </div>
                        <div>
                          <span className="text-white/40">Status:</span>
                          <span className="text-white/70 ml-2 capitalize">{selectedUserUsage.subscription_status}</span>
                        </div>
                        <div>
                          <span className="text-white/40">Created:</span>
                          <span className="text-white/70 ml-2">
                            {selectedUserUsage.created_at ? format(new Date(selectedUserUsage.created_at), 'MMM dd, yyyy') : '-'}
                          </span>
                        </div>
                        {selectedUserUsage.subscription_expires_at && (
                          <div>
                            <span className="text-white/40">Expires:</span>
                            <span className="text-white/70 ml-2">
                              {format(new Date(selectedUserUsage.subscription_expires_at), 'MMM dd, yyyy')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {selectedOptionsActivity && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
            onClick={(event) => { if (event.target === event.currentTarget) setSelectedOptionsActivity(null); }}
            data-testid="options-activity-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Atividade GENI Options"
          >
            <div className="max-h-[80vh] w-full max-w-xl overflow-hidden rounded-sm border border-white/10 bg-[#121212]" data-testid="options-activity-detail-content">
              <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                <div>
                  <h3 className="text-lg font-bold text-white" data-testid="options-activity-detail-title">ATIVIDADE GENI OPTIONS</h3>
                  <p className="mt-1 text-sm text-white/55" data-testid="options-activity-detail-email">{selectedOptionsActivity.email || '—'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedOptionsActivity(null)}
                  className="p-1 text-white/45 transition-colors hover:text-white"
                  data-testid="options-activity-detail-close"
                  aria-label="Fechar atividade"
                >
                  <X size={20} weight="bold" />
                </button>
              </div>

              <div className="max-h-[65vh] space-y-6 overflow-y-auto px-6 py-5">
                {loadingOptionsActivity ? (
                  <div className="flex justify-center py-10" data-testid="options-activity-detail-loading">
                    <CircleNotch size={28} className="animate-spin text-white/45" />
                  </div>
                ) : (
                  <>
                    <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2" data-testid="options-activity-detail-facts">
                      {[
                        ['last-login', 'Último login', selectedOptionsActivity.last_login_at],
                        ['last-options-access', 'Último acesso Options', selectedOptionsActivity.last_options_activity_at],
                        ['last-sync', 'Último Sync', selectedOptionsActivity.last_sync_at],
                        ['plan', 'Plano', getPlanLabel(selectedOptionsActivity)],
                        ['deribit', 'Deribit', selectedOptionsActivity.deribit_connected ? 'Conectada' : 'Não conectada'],
                        ['first-options-use', 'Primeiro uso Options', selectedOptionsActivity.first_options_activity_at],
                      ].map(([key, label, value]) => (
                        <div key={key} className="border-b border-white/8 pb-3" data-testid={`options-activity-detail-${key}`}>
                          <p className="text-xs text-white/42" data-testid={`options-activity-detail-${key}-label`}>{label}</p>
                          <p className="mt-1 text-sm font-medium text-white" data-testid={`options-activity-detail-${key}-value`}>
                            {key.endsWith('at') || ['last-login', 'last-options-access', 'last-sync', 'first-options-use'].includes(key) ? formatDateTime(value) : value}
                          </p>
                        </div>
                      ))}
                    </div>

                    <section data-testid="options-activity-detail-counts">
                      <h4 className="mb-3 text-sm font-bold text-white" data-testid="options-activity-detail-counts-title">USO — ÚLTIMOS 30 DIAS</h4>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
                        {OPTIONS_USAGE_ITEMS.map(([eventName, label]) => (
                          <div key={eventName} className="flex items-center justify-between border-b border-white/8 py-1.5 text-sm" data-testid={`options-activity-count-${eventName}`}>
                            <span className="text-white/58" data-testid={`options-activity-count-${eventName}-label`}>{label}</span>
                            <span className="font-mono font-bold text-white" data-testid={`options-activity-count-${eventName}-value`}>{selectedOptionsActivity.usage_30d?.[eventName] || 0}</span>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section data-testid="options-activity-detail-recent">
                      <h4 className="mb-3 text-sm font-bold text-white" data-testid="options-activity-detail-recent-title">ATIVIDADE RECENTE</h4>
                      {selectedOptionsActivity.recent_activity?.length ? (
                        <div className="space-y-2">
                          {selectedOptionsActivity.recent_activity.map((event, index) => {
                            const label = OPTIONS_USAGE_ITEMS.find(([eventName]) => eventName === event.event_name)?.[1] || event.event_name;
                            return (
                              <div key={`${event.event_name}-${event.timestamp}-${index}`} className="flex items-center justify-between gap-4 text-sm" data-testid={`options-activity-recent-event-${index}`}>
                                <span className="min-w-0 truncate text-white/70" data-testid={`options-activity-recent-event-${index}-name`}>{label}</span>
                                <span className="shrink-0 text-xs text-white/42" data-testid={`options-activity-recent-event-${index}-timestamp`}>{formatDateTime(event.timestamp)}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-white/40" data-testid="options-activity-detail-recent-empty">Sem eventos registrados ainda.</p>
                      )}
                    </section>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* BTC Outlook Admin Tab */}
        {activeTab === 'outlook' && (
          <div className="space-y-6" data-testid="admin-outlook-tab">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6 space-y-6">
              <div className="flex items-center gap-2">
                <Compass size={22} className="text-[#A855F7]" weight="duotone" />
                <h3 className="text-lg font-bold text-white uppercase tracking-wide">BTC Outlook Control</h3>
              </div>

              {loadingOutlook ? (
                <div className="flex justify-center p-6">
                  <GeniLoader size="md" />
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2">
                  
                  {/* Left Column: Form / Publication */}
                  <div className="space-y-5">
                    <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/45">PUBLICAR NOVO OUTLOOK</p>
                    
                    <div className="space-y-3">
                      <label className="block text-xs font-semibold text-white/40 uppercase tracking-[0.18em]">SELECIONE O ESTADO DE MERCADO</label>
                      <div className="grid gap-2 grid-cols-1">
                        {[
                          { value: 'BULLISH', label: 'BULLISH (Alta)' },
                          { value: 'SLIGHTLY_BULLISH', label: 'SLIGHTLY BULLISH (Alta Moderada)' },
                          { value: 'NEUTRAL', label: 'NEUTRAL (Neutro)' },
                          { value: 'SLIGHTLY_BEARISH', label: 'SLIGHTLY BEARISH (Baixa Moderada)' },
                          { value: 'BEARISH', label: 'BEARISH (Baixa)' }
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setSelectedBias(opt.value)}
                            className={`w-full rounded-md border p-3.5 text-left text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                              selectedBias === opt.value
                                ? "border-[#A855F7] bg-[#A855F7]/10 text-white"
                                : "border-white/5 bg-[#0A0A0A] text-white/60 hover:border-white/10 hover:text-white"
                            }`}
                          >
                            <span className="flex items-center justify-between">
                              <span>{opt.label}</span>
                              {selectedBias === opt.value && <span className="text-[#A855F7]">●</span>}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* FUTURES HEDGE SECTION */}
                    <div className="space-y-4 pt-4 border-t border-white/5">
                      <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/45">FUTURES HEDGE (OPCIONAL)</p>
                      
                      <div className="grid gap-4 sm:grid-cols-2">
                        {/* PUT SIDE inputs */}
                        <div className="space-y-3 bg-[#0A0A0A] border border-white/5 p-3 rounded-md">
                          <p className="text-[10px] font-bold text-red-400 tracking-wider">PUT SIDE (PROTEÇÃO QUEDA)</p>
                          <div className="space-y-2">
                            <div>
                              <label className="block text-[10px] font-semibold text-white/40 uppercase mb-1">Aggressive Hedge ($)</label>
                              <input
                                type="text"
                                value={putHedgeAggressive}
                                onChange={(e) => setPutHedgeAggressive(e.target.value)}
                                placeholder="ex. 68000"
                                className="w-full bg-zinc-950 border border-white/10 rounded p-2 text-xs font-bold text-white focus:border-[#A855F7] outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-semibold text-white/40 uppercase mb-1">Conservative Hedge ($)</label>
                              <input
                                type="text"
                                value={putHedgeConservative}
                                onChange={(e) => setPutHedgeConservative(e.target.value)}
                                placeholder="ex. 62000"
                                className="w-full bg-zinc-950 border border-white/10 rounded p-2 text-xs font-bold text-white focus:border-[#A855F7] outline-none"
                              />
                            </div>
                          </div>
                        </div>

                        {/* CALL SIDE inputs */}
                        <div className="space-y-3 bg-[#0A0A0A] border border-white/5 p-3 rounded-md">
                          <p className="text-[10px] font-bold text-emerald-400 tracking-wider">CALL SIDE (PROTEÇÃO ALTA)</p>
                          <div className="space-y-2">
                            <div>
                              <label className="block text-[10px] font-semibold text-white/40 uppercase mb-1">Aggressive Hedge ($)</label>
                              <input
                                type="text"
                                value={callHedgeAggressive}
                                onChange={(e) => setCallHedgeAggressive(e.target.value)}
                                placeholder="ex. 84000"
                                className="w-full bg-zinc-950 border border-white/10 rounded p-2 text-xs font-bold text-white focus:border-[#A855F7] outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-semibold text-white/40 uppercase mb-1">Conservative Hedge ($)</label>
                              <input
                                type="text"
                                value={callHedgeConservative}
                                onChange={(e) => setCallHedgeConservative(e.target.value)}
                                placeholder="ex. 91000"
                                className="w-full bg-zinc-950 border border-white/10 rounded p-2 text-xs font-bold text-white focus:border-[#A855F7] outline-none"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={handlePublishOutlook}
                      disabled={isPublishingOutlook}
                      className="w-full bg-[#A855F7] text-white font-bold tracking-wider uppercase text-xs hover:bg-[#b56cfc] py-4 rounded-md transition-all shadow-lg shadow-purple-500/10"
                    >
                      {isPublishingOutlook ? 'PUBLICANDO...' : 'PUBLICAR OUTLOOK'}
                    </Button>
                  </div>

                  {/* Right Column: Current Status Info */}
                  <div className="space-y-5 border-t border-white/5 pt-6 md:border-t-0 md:pt-0 md:border-l md:border-white/5 md:pl-6">
                    <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/45">STATUS DO OUTLOOK ATUAL</p>

                    {outlookData?.status === 'published' ? (
                      <div className="space-y-4">
                        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4 space-y-3">
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-white/35">Current Outlook</p>
                            <p className="text-lg font-black text-white">{outlookData.label || outlookData.current_bias}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-white/35">Horizon</p>
                            <p className="text-sm font-bold text-white/80">{outlookData.horizon || '30-60 DAYS'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-white/35">Updated</p>
                            <p className="text-sm font-semibold text-white/60">
                              {outlookData.updated_at ? new Date(outlookData.updated_at).toLocaleString() : '—'}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-white/35">Updated By</p>
                            <p className="text-sm font-semibold text-white/60">{outlookData.updated_by || 'admin'}</p>
                          </div>
                        </div>

                        {/* Mapping values preview */}
                        <div className="space-y-2.5">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35">VALORES DERIVADOS (PREVIEW PRO)</p>
                          <div className="text-xs text-white/55 space-y-1 bg-zinc-950/25 p-3.5 border border-white/5 rounded-md">
                            <p><strong className="text-white/70">Exposição:</strong> {outlookData.exposure}</p>
                            <p><strong className="text-white/70">Descrição:</strong> {outlookData.description}</p>
                            <p><strong className="text-white/70">Estratégias:</strong> {outlookData.possible_strategies?.join(', ') || '—'}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-md border border-dashed border-white/10 bg-black/20 p-5 text-center text-white/40 text-xs">
                        Nenhum Outlook publicado no momento.
                      </div>
                    )}
                  </div>

                </div>
              )}
            </div>
          </div>
        )}

        {/* Product Update Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div className="space-y-6" data-testid="subscribers-section">
            {/* Header with stats and export */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Plan Filter */}
              <div className="flex items-center gap-2">
                <Funnel size={16} weight="duotone" className="text-white/40" />
                <span className="text-white/60 text-sm">Plan:</span>
                <div className="flex gap-1">
                  {[
                    { value: 'all', label: `All (${subscriberStats.total})` },
                    { value: 'free', label: `Free (${subscriberStats.by_plan?.free || 0})` },
                    { value: 'pro', label: `Pro (${subscriberStats.by_plan?.pro || 0})` }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSubscriberPlanFilter(option.value)}
                      data-testid={`subscriber-filter-${option.value}`}
                      className={`px-3 py-1.5 text-xs font-mono rounded-sm border transition-colors ${
                        subscriberPlanFilter === option.value
                          ? 'bg-[#00E676]/10 border-[#00E676]/50 text-[#00E676]'
                          : 'bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20 hover:text-white/80'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Export Button */}
              <Button
                onClick={exportSubscribersCSV}
                disabled={filteredSubscribers.length === 0}
                className="bg-[#00E676] text-black hover:bg-[#00E676]/80 font-semibold"
                data-testid="export-subscribers-csv"
              >
                <Database size={16} className="mr-2" />
                Export CSV ({filteredSubscribers.length})
              </Button>
            </div>

            {/* Subscribers Table */}
            <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
              {loadingSubscribers ? (
                <div className="flex items-center justify-center py-12">
                  <GeniLoader size="sm" />
                </div>
              ) : filteredSubscribers.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={32} className="mx-auto mb-3 text-white/20" />
                  <p className="text-white/40">
                    {subscribers.length === 0 
                      ? 'No users have opted in to product updates yet' 
                      : 'No subscribers match this filter'}
                  </p>
                </div>
              ) : (
                <SyncedScrollTable maxHeight="calc(100vh - 350px)">
                  <table className="w-full text-sm" data-testid="subscribers-table">
                    <thead>
                      <tr className="border-b border-white/10 bg-[#0A0A0A]">
                        <th className="text-left px-4 py-3 text-white/60 font-medium">Email</th>
                        <th className="text-left px-4 py-3 text-white/60 font-medium">Plan</th>
                        <th className="text-left px-4 py-3 text-white/60 font-medium">Signed Up</th>
                        <th className="text-left px-4 py-3 text-white/60 font-medium">User ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubscribers.map((subscriber) => (
                        <tr 
                          key={subscriber.user_id} 
                          className="border-b border-white/5 hover:bg-white/5 transition-colors"
                          data-testid={`subscriber-row-${subscriber.user_id}`}
                        >
                          <td className="px-4 py-3">
                            <span className="text-white font-medium">
                              {subscriber.email || 'No email'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {subscriber.subscription_status === 'pro' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 text-[#FFD700] border border-[#FFD700]/30">
                                <Crown size={12} weight="fill" />
                                PRO {subscriber.subscription_plan?.toUpperCase()}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/5 text-white/60 border border-white/10">
                                FREE
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-white/60">
                            {subscriber.created_at ? formatDate(subscriber.created_at) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-white/40 text-xs font-mono truncate max-w-[120px] block" title={subscriber.user_id}>
                              {subscriber.user_id?.slice(0, 12)}...
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </SyncedScrollTable>
              )}
            </div>

            {/* Refresh Button */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={fetchSubscribers}
                className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                data-testid="refresh-subscribers"
              >
                <ArrowClockwise size={16} className="mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'lifecycle' && (
          <div className="space-y-6" data-testid="lifecycle-tab">
            {/* Dry-run disclaimer */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm px-4 py-2 flex items-center gap-2">
              <Warning size={16} className="text-yellow-400" />
              <span className="text-yellow-300 text-sm">
                Modo DRY-RUN ativo{lifecycleDryRun ? '' : ' (desativado)'} — nenhum e-mail real é enviado. Apenas avalia elegibilidade e grava o resultado.
              </span>
            </div>

            {/* Automation - real-send master switch + per-event toggles. Both default OFF. */}
            <div className="bg-[#121212] border border-white/10 rounded-sm p-4" data-testid="lifecycle-automation-section">
              <div className="mb-3">
                <h3 className="text-white font-bold text-sm uppercase tracking-wide">Automation</h3>
                <p className="text-white/40 text-xs mt-1">
                  Controla o envio REAL de emails automáticos do scheduler. "Testar todos os emails" acima não é afetado por estas chaves.
                </p>
              </div>

              {!lifecycleAutomation.master_enabled && (
                <div className="mb-4 px-3 py-2 bg-orange-500/10 border border-orange-500/30 rounded-sm text-orange-300 text-xs" data-testid="automation-dry-run-warning">
                  Lifecycle automation is currently in dry-run only. No automatic emails are being sent.
                </div>
              )}

              {loadingAutomation ? (
                <div className="flex items-center justify-center py-6">
                  <GeniLoader size="sm" />
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between py-2.5 px-3 mb-2 bg-[#0A0A0A] border border-white/10 rounded-sm">
                    <span className="text-white font-semibold text-sm">Master</span>
                    <button
                      onClick={() => confirmSetMasterAutomation(!lifecycleAutomation.master_enabled)}
                      disabled={togglingAutomation !== null}
                      data-testid="automation-master-toggle"
                      className={`px-3 py-1 text-xs font-bold rounded-sm border transition-colors w-14 text-center ${
                        lifecycleAutomation.master_enabled
                          ? 'bg-[#00E676]/10 text-[#00E676] border-[#00E676]/50'
                          : 'bg-white/5 text-white/50 border-white/10'
                      }`}
                    >
                      {togglingAutomation === 'master' ? <CircleNotch size={12} className="animate-spin mx-auto" /> : (lifecycleAutomation.master_enabled ? 'ON' : 'OFF')}
                    </button>
                  </div>

                  <div className="space-y-1">
                    {lifecycleTemplates.map((template) => (
                      <div
                        key={template.id}
                        className="flex items-center justify-between py-2 px-3"
                        data-testid={`automation-event-row-${template.id}`}
                      >
                        <span className="text-white/70 text-xs">{template.label}</span>
                        <button
                          onClick={() => confirmSetEventAutomation(template.id, !lifecycleAutomation.event_enabled?.[template.id])}
                          disabled={togglingAutomation !== null}
                          data-testid={`automation-event-toggle-${template.id}`}
                          className={`px-3 py-1 text-xs font-bold rounded-sm border transition-colors w-14 text-center ${
                            lifecycleAutomation.event_enabled?.[template.id]
                              ? 'bg-[#00E676]/10 text-[#00E676] border-[#00E676]/50'
                              : 'bg-white/5 text-white/50 border-white/10'
                          }`}
                        >
                          {togglingAutomation === template.id ? <CircleNotch size={12} className="animate-spin mx-auto" /> : (lifecycleAutomation.event_enabled?.[template.id] ? 'ON' : 'OFF')}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Email Templates (Phase 2E) - preview/QA only, uses the real production renderers */}
            <div className="bg-[#121212] border border-white/10 rounded-sm p-4" data-testid="lifecycle-templates-section">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-white font-bold text-sm uppercase tracking-wide">Email Templates</h3>
                  <p className="text-white/40 text-xs mt-1">
                    Previews reais enviados somente para <span className="font-mono">{LIFECYCLE_TEST_RECIPIENT_EMAIL}</span>. Nenhum usuário recebe estes emails.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Language toggle - controls the language of every template preview sent below */}
                  <div className="flex items-center gap-1 bg-[#0A0A0A] border border-white/10 rounded-sm p-0.5" data-testid="lifecycle-template-lang-toggle">
                    {[
                      { value: 'pt', label: 'PT' },
                      { value: 'en', label: 'EN' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setTemplateTestLang(option.value)}
                        disabled={testingTemplateId !== null}
                        data-testid={`lifecycle-template-lang-${option.value}`}
                        className={`px-3 py-1.5 text-xs font-mono rounded-sm transition-colors ${
                          templateTestLang === option.value
                            ? 'bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/50'
                            : 'text-white/50 border border-transparent hover:text-white/80'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <Button
                    onClick={confirmTestAllLifecycleTemplates}
                    disabled={testingTemplateId !== null}
                    className="bg-[#00E676] text-black hover:bg-[#00E676]/80 font-semibold"
                    data-testid="lifecycle-test-all-button"
                  >
                    {testingTemplateId === 'all' ? (
                      <CircleNotch size={16} className="mr-2 animate-spin" />
                    ) : (
                      <EnvelopeSimple size={16} className="mr-2" />
                    )}
                    Testar todos os emails
                  </Button>
                </div>
              </div>

              {loadingLifecycleTemplates ? (
                <div className="flex items-center justify-center py-8">
                  <GeniLoader size="sm" />
                </div>
              ) : (
                <div className="space-y-2">
                  {lifecycleTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="flex items-center justify-between bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-2.5"
                      data-testid={`lifecycle-template-row-${template.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-white text-sm font-medium">{template.label}</span>
                        <span className="px-2 py-0.5 text-xs font-bold rounded bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/30 uppercase">
                          {template.status === 'ready' ? 'Ready' : template.status}
                        </span>
                      </div>
                      <Button
                        onClick={() => testLifecycleTemplate(template.id)}
                        disabled={testingTemplateId !== null}
                        variant="outline"
                        size="sm"
                        className="bg-transparent border-white/20 hover:bg-white/5 text-white/80 hover:text-white shrink-0"
                        data-testid={`lifecycle-template-test-btn-${template.id}`}
                      >
                        {testingTemplateId === template.id && (
                          <CircleNotch size={14} className="mr-1.5 animate-spin" />
                        )}
                        Test
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Last run summary + Run Now */}
            <div className="bg-[#121212] border border-white/10 rounded-sm p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-6">
                <div>
                  <div className="text-white/40 text-xs mb-1 flex items-center gap-1">
                    <Clock size={12} /> Última execução
                  </div>
                  <div className="text-white font-mono text-sm" data-testid="lifecycle-last-run">
                    {lifecycleRuns[0] ? formatDateTime(lifecycleRuns[0].started_at) : '-'}
                  </div>
                </div>
                <div>
                  <div className="text-white/40 text-xs mb-1">Trigger</div>
                  <div className="text-white/70 text-sm font-mono">
                    {lifecycleRuns[0]?.trigger || '-'}
                  </div>
                </div>
                <div>
                  <div className="text-white/40 text-xs mb-1">Usuários avaliados</div>
                  <div className="text-white text-sm font-mono">{lifecycleRuns[0]?.users_evaluated ?? '-'}</div>
                </div>
              </div>
              <Button
                onClick={runLifecycleNow}
                disabled={runningLifecycleNow}
                className="bg-[#00E676] text-black hover:bg-[#00E676]/80 font-semibold"
                data-testid="lifecycle-run-now-button"
              >
                {runningLifecycleNow ? (
                  <CircleNotch size={16} className="mr-2 animate-spin" />
                ) : (
                  <Play size={16} className="mr-2" />
                )}
                Rodar agora
              </Button>
            </div>

            {/* Summary counters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                <div className="text-white/40 text-xs mb-1">Ativação</div>
                <div className="text-2xl font-bold font-mono text-white" data-testid="lifecycle-count-activation">
                  {lifecycleEventCounts.activation}
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                <div className="text-white/40 text-xs mb-1">Marcos (Milestones)</div>
                <div className="text-2xl font-bold font-mono text-white" data-testid="lifecycle-count-milestone">
                  {lifecycleEventCounts.milestone}
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                <div className="text-white/40 text-xs mb-1">Inatividade</div>
                <div className="text-2xl font-bold font-mono text-white" data-testid="lifecycle-count-inactivity">
                  {lifecycleEventCounts.inactivity}
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                <div className="text-white/40 text-xs mb-1">Resumo Mensal</div>
                <div className="text-2xl font-bold font-mono text-white" data-testid="lifecycle-count-monthly-summary">
                  {lifecycleEventCounts.monthly_summary}
                </div>
              </div>
            </div>

            {/* Event type filter */}
            <div className="flex items-center gap-2">
              <Funnel size={16} weight="duotone" className="text-white/40" />
              <span className="text-white/60 text-sm">Evento:</span>
              <div className="flex gap-1">
                {[
                  { value: 'all', label: `Todos (${latestLifecycleRunResults.length})` },
                  { value: 'activation', label: `Ativação (${lifecycleEventCounts.activation})` },
                  { value: 'milestone', label: `Marcos (${lifecycleEventCounts.milestone})` },
                  { value: 'inactivity', label: `Inatividade (${lifecycleEventCounts.inactivity})` },
                  { value: 'monthly_summary', label: `Resumo Mensal (${lifecycleEventCounts.monthly_summary})` }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setLifecycleEventFilter(option.value)}
                    data-testid={`lifecycle-filter-${option.value}`}
                    className={`px-3 py-1.5 text-xs font-mono rounded-sm border transition-colors ${
                      lifecycleEventFilter === option.value
                        ? 'bg-[#00E676]/10 border-[#00E676]/50 text-[#00E676]'
                        : 'bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20 hover:text-white/80'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dry-run results table - scoped to the latest run only (resolved events, not raw candidates) */}
            <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
              <div className="px-4 py-2 border-b border-white/10 text-white/40 text-xs">
                Mostrando eventos resolvidos da última execução (1 evento por usuário no máximo)
              </div>
              {loadingLifecycle ? (
                <div className="flex items-center justify-center py-12">
                  <GeniLoader size="sm" />
                </div>
              ) : filteredLifecycleResults.length === 0 ? (
                <div className="text-center py-12">
                  <EnvelopeSimple size={32} className="mx-auto mb-3 text-white/20" />
                  <p className="text-white/40">Nenhum resultado de dry-run ainda. Clique em "Rodar agora".</p>
                </div>
              ) : (
                <SyncedScrollTable maxHeight="calc(100vh - 350px)">
                  <table className="w-full text-sm" data-testid="lifecycle-results-table">
                    <thead>
                      <tr className="border-b border-white/10 bg-[#0A0A0A]">
                        <th className="text-left px-4 py-3 text-white/60 font-medium">Usuário</th>
                        <th className="text-left px-4 py-3 text-white/60 font-medium">Evento</th>
                        <th className="text-left px-4 py-3 text-white/60 font-medium">Idioma</th>
                        <th className="text-left px-4 py-3 text-white/60 font-medium">Motivo</th>
                        <th className="text-left px-4 py-3 text-white/60 font-medium">Avaliado em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLifecycleResults.map((result) => (
                        <tr
                          key={result.id}
                          className="border-b border-white/5 hover:bg-white/5 transition-colors"
                          data-testid={`lifecycle-result-row-${result.id}`}
                        >
                          <td className="px-4 py-3">
                            <span className="text-white font-medium">{result.email || result.user_id}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 text-xs font-bold rounded uppercase ${
                              result.event_type === 'activation'
                                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                : result.event_type === 'milestone'
                                ? 'bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30'
                                : result.event_type === 'monthly_summary'
                                ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                                : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                            }`}>
                              {result.event_key}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-testid={`lifecycle-result-locale-${result.id}`}>
                            {result.locale ? (
                              <span
                                className="text-xs font-mono font-bold text-white/70"
                                title={result.locale_is_fallback ? 'Idioma padrão/fallback' : 'Idioma salvo pelo usuário'}
                              >
                                {result.locale === 'ptBR' ? 'PT' : 'EN'}{result.locale_is_fallback ? '*' : ''}
                              </span>
                            ) : (
                              <span className="text-white/20 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-white/60">{result.reason}</td>
                          <td className="px-4 py-3 text-white/40 font-mono text-xs">
                            {formatDateTime(result.evaluated_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </SyncedScrollTable>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Referrer Owed Details Modal */}
      {referrerDetailsModal.open && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setReferrerDetailsModal({ open: false, referrer: null, owedConversions: [], loading: false });
            }
          }}
        >
          <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-3xl max-h-[80vh] overflow-hidden mx-4">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Wallet size={20} className="text-[#FFC107]" />
                  Owed Commissions
                </h3>
                {referrerDetailsModal.referrer && (
                  <div className="mt-1 flex items-center gap-3">
                    <span className="text-white/60 text-sm">{referrerDetailsModal.referrer.referrer_email}</span>
                    <span className="text-[#00E676] font-mono text-sm font-bold">{referrerDetailsModal.referrer.referral_code}</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setReferrerDetailsModal({ open: false, referrer: null, owedConversions: [], loading: false })}
                className="text-white/40 hover:text-white p-1"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4 overflow-y-auto max-h-[50vh]">
              {referrerDetailsModal.loading ? (
                <div className="flex items-center justify-center py-8">
                  <CircleNotch size={32} className="animate-spin text-white/40" />
                </div>
              ) : referrerDetailsModal.owedConversions.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle size={48} className="mx-auto mb-3 text-[#00E676]/40" />
                  <p className="text-white/60">No owed commissions</p>
                  <p className="text-white/40 text-sm mt-1">All commissions have been paid</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {referrerDetailsModal.owedConversions.map((conv, idx) => (
                    <div 
                      key={idx}
                      className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4 flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <div className="text-white text-sm">{conv.referred_email}</div>
                        <div className="text-white/40 text-xs font-mono mt-1">
                          {conv.payment_approved_at ? formatDateTime(conv.payment_approved_at) : '-'}
                        </div>
                      </div>
                      <div className="text-center px-4">
                        <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                          conv.plan_type === 'yearly' 
                            ? 'bg-[#FFC107]/20 text-[#FFC107]' 
                            : 'bg-white/10 text-white/70'
                        }`}>
                          {conv.plan_type?.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-white font-mono">${conv.payment_amount_usd?.toFixed(2)}</div>
                        <div className="text-[#FFC107] font-mono font-bold text-sm">+${conv.reward_amount_usd?.toFixed(2)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {!referrerDetailsModal.loading && referrerDetailsModal.owedConversions.length > 0 && (
              <div className="px-6 py-4 border-t border-white/10 bg-[#0A0A0A]">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white/40 text-xs">Total Owed</div>
                    <div className="text-2xl font-bold font-mono text-[#FFC107]">
                      ${referrerDetailsModal.totalOwed?.toFixed(2)}
                    </div>
                    <div className="text-white/40 text-xs mt-1">
                      {referrerDetailsModal.owedCount} conversion{referrerDetailsModal.owedCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {referrerDetailsModal.walletAddress && (
                      <div className="text-right">
                        <div className="text-white/40 text-xs mb-1">Wallet</div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-white/60">
                            {referrerDetailsModal.walletAddress.slice(0, 8)}...{referrerDetailsModal.walletAddress.slice(-6)}
                          </span>
                          <button
                            onClick={() => copyWalletAddress(referrerDetailsModal.walletAddress)}
                            className="p-1 text-white/40 hover:text-white hover:bg-white/10 rounded transition-colors"
                            title="Copy wallet address"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => markAllReferrerPaid(
                        referrerDetailsModal.referrer.referrer_user_id,
                        referrerDetailsModal.referrer.referral_code,
                        referrerDetailsModal.totalOwed,
                        referrerDetailsModal.owedCount
                      )}
                      disabled={actionLoading === `bulk-${referrerDetailsModal.referrer?.referrer_user_id}`}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded bg-[#00E676] text-black hover:bg-[#00E676]/90 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === `bulk-${referrerDetailsModal.referrer?.referrer_user_id}` ? (
                        <CircleNotch size={16} className="animate-spin" />
                      ) : (
                        <Check size={16} weight="bold" />
                      )}
                      Mark All Paid
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && closeConfirmDialog()}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={executeConfirmedAction}
              className={confirmDialog.variant === 'destructive' 
                ? "bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80"
                : "bg-white text-black hover:bg-gray-200"
              }
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
